import * as THREE from "three";
import { Game } from "../../../game.js";
import { Screen } from "../../../screen_manager/screen.js";
import { ScreenManager, Screens } from "../../../screen_manager/screen_manager.js";
import { DialogManager } from "../../dialog/dialog_manager.js";
import { TournamentService } from "../../../tournament/tournament_service.js";
import { PlayDialog, PlayDialogState, PlayDialogDifficulty } from "../../dialog/play_dialog.js";
import { DailyChallengePlayDialog } from "../../dialog/daily_challenge_play_dialog.js";
import { LeaveLevelDialog } from "../../dialog/leave_level_dialog.js";
import { getLevelDifficulty, getLevelConfig, LevelConfig, BarrierConfig, TableConfig, getMaxCampaignLevel, hasCampaignLevel, areBundledLevelsAllowed, LevelLayer, LevelPiece, LevelPieceType, MAX_LEVEL_LAYERS, MAX_TABLE_LAYERS, MAX_TABLES, maxTableGridCols } from "../../../level_config.js";
import { OverlayManager } from "../../overlay/overlay_manager.js";
import { preloadAdsInBackground } from "../../../ads/first_load_manager.js";
import { SceneManager } from "../../../core/scene_manager.js";
import { SceneDimmer } from "../../../core/scene_dimmer.js";
import { Renderer } from "../../../core/renderer.js";
import { CameraController } from "../../../core/camera_controller.js";
import { PhysicsCube, CubeMaterialType } from "../../../entities/physics_cube.js";
import { JamJar } from "../../../entities/jam_jar.js";
import { PhysicsCone } from "../../../entities/physics_cone.js";
import { MetalBlock, MetalBlockShape } from "../../../entities/metal_block.js";
import { IceBlock } from "../../../entities/ice_block.js";
import { TreeBlock } from "../../../entities/tree_block.js";
import { WatermelonBlock } from "../../../entities/watermelon_block.js";
import { Barrier, BarrierHead, BarrierMount } from "../../../entities/barrier.js";
import { PhysicsTable } from "../../../entities/physics_table.js";
import { Cannon } from "../../../entities/cannon.js";
import { Ground } from "../../../entities/ground.js";
import { shuffle } from "../../../utils/helpers.js";
import { Vfx3DRuntime } from "../../../vfx_3d/vfx_3d_runtime.js";
import { Vfx3DManager } from "../../../vfx_3d/vfx_3d_manager.js";
import { StatisticsStorage } from "../../../storage/statistics_storage.js";
import { DAILY_CHALLENGE_DAYS_COUNT } from "../../../daily_challenge/daily_challenge_config.js";
import { WinStreakTowerService } from "../../../win_streak_tower/win_streak_tower_service.js";
import { getWinStreakProgress, WinStreakProgress } from "../adventure_screen/adventure_floor_info.js";
import { TowerFailDialog } from "../../dialog/tower_fail_dialog.js";
import type { ReviveOfferDto } from "../../../api/dto/player.dto.js";
import { recordInterstitialLevelPassed, tryShowInterstitial } from "../../../ads/interstitial.js";
import { analytics, AnalyticsEventName, AnalyticsPropertyKey } from "../../../analytics/index.js";
import
  {
    logAfCompleteTutorial,
    logGameEnd,
    logGameExit,
    logGameStart,
    logLevelCompleted,
    logRewardedCompleted,
    logVirtualCurrencyEarned,
    logVirtualCurrencySpent
  } from "../../../analytics/appsflyer_events.js";
import { explainMissingCampaignLevel } from "../../../level_pack/level_gate.js";
import { DailyChallengeService } from "../../../daily_challenge/daily_challenge_service.js";
import { generateRandomId } from "../../../analytics/analytics_ad_module.js";
import { AnalyticStore } from "../../../analytics/analytic_store.js";
import { AnalyticsGameType, AnalyticsLevelResult } from "../../../analytics/analytics_types.js";
import { nextGameNumber, recordLevelAttempt, takeLevelAttempts } from "../../../analytics/analytics_identity.js";
import { balanceSnapshot } from "../../../analytics/balance_snapshot.js";
import { audioController } from "../../../audio/audio_controller.js";
import { AudioClips } from "../../../audio/audio_constants.js";
import { hapticsController, VibrationContext } from "../../../haptics/haptics_controller.js";
import { HapticPattern } from "../../../haptics/haptics_constants.js";
import { reportLevelFailed, completeLevel, doubleLevelReward, getReviveOffer, reportLevelStart } from "../../../offline/economy_gateway.js";
import { OfferTriggerController } from "../../../offers/offer_trigger_controller.js";
import { OverlayFactory } from "../../overlay/overlay_factory.js";
import { OverlayId } from "../../overlay/overlay_ids.js";
import { GameResultOverlay, GameResultOverlayParams } from "../../overlay/overlays/game_result_overlay.js";
import { isHardUpdatePending } from "../../../app_config/app_update_check.js";
import { ContinueOverlayParams } from "../../overlay/overlays/continue_overlay.js";
import { RateGameDialog } from "../../dialog/rate_game_dialog.js";
import { AppConfigService } from "../../../app_config/app_config_service.js";
import { adoptEconomyFromPlayer, nextHeartAtMs } from "../../../api/player_sync.js";
import { LeaderboardSyncService } from "../../../api/leaderboard/leaderboard_sync_service.js";
import { GameBot, BotGameApi, BotTarget } from "../../../simulation/game_bot.js";
import { SimulationRunResult } from "../../../simulation/simulation_context.js";
import { LevelData, StackItem, STATIC_LEVELS } from "./static_levels.js";
import { TestLevelBridge } from "../../../debug/test_level_bridge.js";
import { StressTest } from "../../../debug/stress_test.js";
import { TextureTest } from "../../../debug/texture_test.js";
import { TableFormationShowcase } from "../../../debug/table_formation_showcase.js";
import { BlockShowcase } from "../../../debug/block_showcase.js";
import { NetworkManager } from "../../../network_manager.js";
import { BarrierShowcase } from "../../../debug/barrier_showcase.js";
import { LevelPackService } from "../../../level_pack/level_pack_service.js";
import { setBlockMassMultiplier } from "../../../physics/physics_masses.js";
import { EntityKind, LevelEntity } from "../../../types/level_entity.js";
import { PhysicsWorld } from "../../../physics/physics_world.js";
import { restsOnFaces } from "../../../physics/edge_support_pad.js";
import { BreakablePhysicsEntity } from "../../../entities/breakable_physics_entity.js";
import { VfxBlockShatterTask } from "../../../vfx_3d/effects/block_shatter/vfx_block_shatter_task.js";
import { VfxJamSpillTask } from "../../../vfx_3d/effects/jam/vfx_jam_spill_task.js";
import { FpsOverlay } from "../../../debug/fps_overlay.js";
import { applyPixelFeatures } from "../../../debug/pixel_features.js";
import { ResourceManager } from "../../../resource_loader/resource_manager.js";
import { ResourceID } from "../../../resource_loader/resource_id.js";
import { CannonBall } from "../../../entities/cannon_ball.js";
import { FieldRotator } from "./field_rotator.js";
import { HANG_CONFIG, HangingRig } from "../../../entities/hanging_rig.js";
import { TableSwinger } from "./table_swinger.js";
import { ELEVATOR_CONFIG, TableElevator } from "./table_elevator.js";
import { SLIDE_CONFIG, TableSlider } from "./table_slider.js";
import { blockOverhang, chooseFieldLayout, layoutOverlaps, modelSizeDrift, tableClaim, TableClaim, TableFit, towerTopHeight } from "./field_layout.js";
import { GlobalSettings } from "../../../global_settings.js";
import { GraphicsApply } from "../../../graphics/graphics_apply.js";
import { GraphicsProfile, GraphicsQuality, GraphicsSettings } from "../../../graphics/graphics_quality.js";
import { RimLightTest } from "../../../debug/rim_light_test.js";
import RAPIER from "@dimforge/rapier3d-compat";
import { CumulativeBoosterID } from "../../../cumulative_boosters/cumulative_booster_id.js";
import { completeBoosterTutorial } from "../../../cumulative_boosters/booster_tutorial_progress.js";
import { CumulativeBoosterService } from "../../../cumulative_boosters/cumulative_booster_service.js";
import { MultiballTarget } from "../../../cumulative_boosters/boosters/multiball_booster.js";
import { FireworksTarget } from "../../../cumulative_boosters/boosters/fireworks_booster.js";
import type { StickyMineSettings } from "../../../cumulative_boosters/boosters/sticky_mines_booster.js";
import { StickyMine, StickyMineAttachment } from "../../../entities/sticky_mine.js";
import { SimulationContext } from "../../../simulation/simulation_context.js";
import { BoosterHighlightRenderer } from "../../overlay/booster_highlight_renderer.js";
import { GameBoosterHost, GameBoosterManager } from "./game_booster_manager.js";
import { CustomEventsController } from "../../../socket_handler/controllers/custom_events_controller.js";

/**
 * Room left on a table by the shelf standing on it — see TableConfig.standOn.
 * A ceiling its blocks must stay under, and the XZ patch its leg occupies.
 */
interface ShelfLimits
{
  ceiling_y: number;
  min_x: number;
  max_x: number;
  min_z: number;
  max_z: number;
}

export class GameScreen extends Screen implements BotGameApi, GameBoosterHost
{
  private readonly STARS_PER_WIN = 3;
  private readonly JAR_SPAWN_CHANCE = 0.35;
  private readonly JAR_DOUBLE_CHANCE = 0.35;
  private readonly JAR_TRIPLE_CHANCE = 0.2;
  private readonly METAL_SPAWN_CHANCE = 0.25;
  private readonly METAL_DOUBLE_CHANCE = 0.35;
  private readonly METAL_TRIPLE_CHANCE = 0.2;
  private readonly ICE_SPAWN_CHANCE = 0.2;
  private readonly ICE_DOUBLE_CHANCE = 0.35;
  private readonly ICE_TRIPLE_CHANCE = 0.2;
  private readonly CUBE_DOUBLE_CHANCE = 0.25;
  private readonly CUBE_TRIPLE_CHANCE = 0.12;
  private readonly WIN_RESULT_DELAY_MS = 650;

  private static shaders_warmed: boolean = false;
  private static warmup_materials: THREE.Material[] = [];

  static
  {
    Renderer.onRecreate( () => {
      GameScreen.shaders_warmed = false;
      GameScreen.warmup_materials.length = 0;
    } );
  }

  // piece.variant maps to the current PhysicsCube.VARIANTS indices. Keep these
  // aligned when a resource is removed from that array.
  private static readonly CUBE_SKIN_VARIANT_INDEX = [9, 1, 2];
  private static readonly PLASTIC_SKIN_VARIANT_INDEX = [3, 4, 5, 6, 7, 8, 10, 13];

  private static isDebugRun(): boolean
  {
    return TestLevelBridge.isActive() || StressTest.isActive() || BlockShowcase.isActive() || TextureTest.isActive()
      || TableFormationShowcase.isActive() || BarrierShowcase.isActive();
  }

  private static toAnalyticsDifficulty( difficulty: PlayDialogDifficulty ): 'normal' | 'hard' | 'super_hard'
  {
    return difficulty === PlayDialogDifficulty.SuperHard ? 'super_hard'
      : difficulty === PlayDialogDifficulty.Hard ? 'hard'
      : 'normal';
  }

  private static hasBody( entity: LevelEntity ): boolean
  {
    return entity.hasPhysicsBody?.() !== false;
  }

  private static readonly SHOTS_BY_DIFFICULTY: Record<PlayDialogDifficulty, number> = {
    [PlayDialogDifficulty.Common]: 20,
    [PlayDialogDifficulty.Hard]: 18,
    [PlayDialogDifficulty.SuperHard]: 16,
  };

  private cubes: PhysicsCube[] = [];
  private jars: JamJar[] = [];
  private cones: PhysicsCone[] = [];
  private metal_blocks: MetalBlock[] = [];
  private ice_blocks: IceBlock[] = [];
  private tree_blocks: TreeBlock[] = [];
  private watermelons: WatermelonBlock[] = [];
  // Obstacles, not blocks: kept out of `entities` so they never keep a
  // cleared level alive (see checkGameEnd) or count as score targets.
  private barriers: Barrier[] = [];
  // How far a sky-mounted pillar reaches above its bearing. Only has to
  // clear the top of the frame — the rod above that is never seen.
  private static readonly BARRIER_SKY_DROP_CELLS = 12;
  /** How far past the side edge of the frame a Left / Right barrier rod runs. */
  private static readonly BARRIER_SIDE_OVERSHOOT_CELLS = 2;

  private cannon!: Cannon;
  private camera_controller!: CameraController;

  private tables: PhysicsTable[] = [];
  private field_rotators: FieldRotator[] = [];
  private table_swingers: TableSwinger[] = [];
  private table_elevators: TableElevator[] = [];
  private table_sliders: TableSlider[] = [];
  private hanging_rigs: HangingRig[] = [];
  private aim_body_handles = new Set<number>();

  private static readonly LEVEL_FRAME_FILL = 0.95;
  private static readonly LEVEL_FRAME_OVERFLOW = 1.05;
  private static readonly MIN_SHOT_DEPTH = 10;
  private static readonly CANNON_MIN_Y = 0.5;
  private static readonly MAX_CUBE_SCREEN_HEIGHT = 0.0454;
  private static readonly MIN_CUBE_SCREEN_HEIGHT = 0.0385;
  private static readonly LAYOUT_BUDGET_SHRINK = 0.89;
  private static readonly TABLE_SCREEN_Y = 0.75;
  private static readonly FRAME_DISC_SEGMENTS = 32;
  private static readonly BALL_FAR_MARGIN = 3;
  private static readonly UP = new THREE.Vector3( 0, 1, 0 );

  private top_body_to_table = new Map<number, number>();
  private carried_balls: RAPIER.RigidBody[][] = [];
  private ground!: Ground;

  private sun!: THREE.DirectionalLight;

  private table_frames: { table: PhysicsTable; top: number; center: THREE.Vector3; size: THREE.Vector3; leg_size: THREE.Vector3 }[] = [];

  private debug_empty_level = false;
  private static readonly LOSS_SETTLE_TIMEOUT = 2;
  /** Wins on the current tower floor below which a broken streak is not worth a dialog. */
  private static readonly MIN_TOWER_FAIL_STREAK = 2;
  private loss_settle_timer = 0;
  private debug_end_state_timer = 0;
  private tallest_block_top_y = 0;
  private readonly ball_far_dir = new THREE.Vector3();
  private ball_far_limit = Infinity;

  private readonly tmp_impact = new THREE.Vector3();
  private readonly ball_local_pos = new THREE.Vector3();

  private readonly block_table_index = new WeakMap<object, number>();
  private readonly block_grid_placement = new WeakMap<LevelEntity, {
    table_index: number;
    layer: number;
    cells: { col: number; row: number }[];
  }>();

  private tableFor( entity: object ): PhysicsTable
  {
    return this.tables[ this.block_table_index.get( entity ) ?? 0 ];
  }

  private aiming = false;
  private blocked_tap_hit_test: ( ( client_x: number, client_y: number ) => boolean ) | null = null;
  private input_blocked_by_tutorial = false;
  private input_blocked_by_level_start_booster = false;

  private readonly table_position = new THREE.Vector3(
    0,
    -0.4,
    2
  );
  private readonly TABLE_BASE_ASPECT = 1080 / 1920;
  private readonly TABLE_TALL_ASPECT = 0.4;
  private readonly TABLE_WIDE_ASPECT = 0.75;
  private readonly TABLE_EDGE_Z = 1.25;

  private shots_left = 0;
  private infinite_balls_active = false;
  private cannon_ball_size_multiplier = 1;
  private cannon_ball_mass_multiplier = 1;
  private multiball_active = false;
  private fireworks_active = false;
  private infinite_balls_music_muted = false;
  private readonly booster_manager: GameBoosterManager;
  private is_finished = false;

  private level!: number;
  private analytics_game_id = "";
  private analytics_game_started_at = 0;
  private analytics_initial_cube_count = 0;
  private analytics_game_number = 0;
  private analytics_start_moves = 0;
  /** Release level uid behind this.level (empty for bundled/challenge levels). */
  private analytics_level_uid = "";
  private analytics_mass: Record<string, number | string> = {};
  private analytics_shots_fired = 0;
  private booster_used_this_attempt = false;
  private analytics_moves_purchased_times = 0;
  private analytics_finish_tracked = false;
  private heart_settlement: Promise<void> | null = null;
  private attempt_completed = false;
  private attempt_number = 1;
  private win_result_timeout: number | null = null;
  private level_start_booster_id: CumulativeBoosterID | null = null;
  private defer_level_start_booster = false;
  private sticky_mine_targets: StickyMineAttachment[] = [];
  private sticky_mines: StickyMine[] = [];

  private is_challenge: boolean = false;

  private simulation = false;
  private bot: GameBot | null = null;
  private on_simulation_finished: ( r: SimulationRunResult ) => void = () => {};

  private entities: LevelEntity[] = [];
  private by_collider = new Map<number, LevelEntity>();
  private needs_cleanup = false;

  public onShotsLeftChanged: ( shots_left: number ) => void = () => {};
  public onInvalidShot: ( event: PointerEvent ) => void = () => {};
  public onDifficultyChanged: ( difficulty: PlayDialogDifficulty, level: number ) => void = () => {};
  public onLevelBuilt: () => void = () => {};
  private level_built_pending = false;
  public onAimStart: () => boolean = () => false;
  public onBoosterCountChanged: ( id: CumulativeBoosterID, count: number ) => void = () => {};
  public onResultOverlayShown: () => void = () => {};
  public onAttemptFinished: () => void = () => {};

  public constructor()
  {
    super();

    this.booster_manager = new GameBoosterManager( this );
  }

  public setInfiniteBallsActive( active: boolean ): void
  {
    this.infinite_balls_active = active;

    if ( !active && this.infinite_balls_music_muted )
    {
      this.infinite_balls_music_muted = false;
      audioController.unmuteMusicTemporarily();
    }

    if ( active )
    {
      this.loss_settle_timer = 0;
    }
  }

  public setCannonBallModifiers( size_multiplier: number, mass_multiplier: number ): void
  {
    this.cannon_ball_size_multiplier = size_multiplier;
    this.cannon_ball_mass_multiplier = mass_multiplier;
  }

  public setBigBallChargeActive( active: boolean ): void
  {
    this.cannon.setBigBallChargeActive( active );
  }

  public setFireworksActive( active: boolean ): void
  {
    this.fireworks_active = active;
  }

  public getRandomFireworksTargets(
    count: number,
    excluded_targets: readonly FireworksTarget[] = []
  ): FireworksTarget[]
  {
    if ( count <= 0 )
      return [];

    const cube_size = PhysicsCube.getSize();
    const step_x = cube_size.x * ( 1 + PhysicsCube.GRID_GAP_RATIO );
    const step_z = cube_size.z * ( 1 + PhysicsCube.GRID_GAP_RATIO );
    const candidates = shuffle(
      this.entities
        .filter( entity => entity.isAlive() && GameScreen.hasBody( entity ) )
        .map( entity =>
        {
          const position = entity.body.translation();
          const table_index = this.block_table_index.get( entity ) ?? 0;
          return {
            target: { x: position.x, y: position.y, z: position.z },
            table_index,
            local: this.tables[table_index].toUnrotatedFrame( position, new THREE.Vector3() )
          };
        } )
    );

    if ( candidates.length === 0 )
      return [];

    const reserved = candidates.filter( candidate => excluded_targets.some( excluded =>
      Math.abs( candidate.target.x - excluded.x ) < 0.001
      && Math.abs( candidate.target.y - excluded.y ) < 0.001
      && Math.abs( candidate.target.z - excluded.z ) < 0.001
    ) );
    const targets: FireworksTarget[] = [];

    for ( const candidate of candidates )
    {
      if ( reserved.some( other =>
        other.table_index === candidate.table_index
        && Math.abs( other.local.x - candidate.local.x ) <= step_x * 1.05
        && Math.abs( other.local.y - candidate.local.y ) <= cube_size.y * 1.05
        && Math.abs( other.local.z - candidate.local.z ) <= step_z * 1.05
      ) )
        continue;

      targets.push( { ...candidate.target } );
      reserved.push( candidate );

      if ( targets.length >= count )
        break;
    }

    return targets;
  }

  public fireFireworkAt( target: FireworksTarget, passive_rocket = false ): void
  {
    this.cannon.fireAtWorldPoint(
      new THREE.Vector3( target.x, target.y, target.z ),
      1,
      1,
      true,
      0,
      passive_rocket
    );
    void audioController.playSound( AudioClips.BIG_SHOT );
    void audioController.playSound( AudioClips.ROCKET_FLY );
  }

  public prepareStickyMineTargets( count: number ): number
  {
    this.sticky_mine_targets = [];
    const camera = SceneManager.camera;
    if ( !camera )
      return 0;

    const world = PhysicsWorld.getWorld();
    const cube_size = PhysicsCube.getSize();
    const eye = camera.getWorldPosition( new THREE.Vector3() );
    const used = new Set<LevelEntity>();

    // Spread-out picks first, then the block the camera actually sees in
    // that direction, so a mine never lands on a hidden back-layer face.
    for ( const pick of this.getRandomFireworksTargets( count ) )
    {
      const dir = new THREE.Vector3( pick.x, pick.y, pick.z ).sub( eye );
      const distance = dir.length();
      dir.divideScalar( distance );
      const ray = new RAPIER.Ray( eye, dir );
      const hit = world.castRayAndGetNormal( ray, distance + cube_size.z * 2, true,
        undefined, undefined, undefined, undefined,
        collider => !( collider.parent()?.isFixed() ?? true ) );
      let entity = hit ? this.by_collider.get( hit.collider.handle ) : undefined;
      let point: THREE.Vector3;
      let normal: THREE.Vector3;

      if ( hit && entity && entity.isAlive() && GameScreen.hasBody( entity ) && !used.has( entity ) )
      {
        const at = ray.pointAt( hit.timeOfImpact );
        point = new THREE.Vector3( at.x, at.y, at.z );
        normal = new THREE.Vector3( hit.normal.x, hit.normal.y, hit.normal.z );
      }
      else
      {
        entity = this.entities.find( candidate =>
        {
          if ( used.has( candidate ) || !candidate.isAlive() || !GameScreen.hasBody( candidate ) )
            return false;
          const p = candidate.body.translation();
          return Math.abs( p.x - pick.x ) < 1e-3 && Math.abs( p.y - pick.y ) < 1e-3 && Math.abs( p.z - pick.z ) < 1e-3;
        } );
        if ( !entity )
          continue;
        normal = dir.clone().negate();
        point = new THREE.Vector3( pick.x, pick.y, pick.z ).addScaledVector( normal, cube_size.z * 0.5 );
      }

      used.add( entity );
      const position = entity.body.translation();
      const rotation = entity.body.rotation();
      const inverse = new THREE.Quaternion( rotation.x, rotation.y, rotation.z, rotation.w ).invert();
      this.sticky_mine_targets.push( {
        entity,
        local_point: point.sub( new THREE.Vector3( position.x, position.y, position.z ) ).applyQuaternion( inverse ),
        local_normal: normal.applyQuaternion( inverse ).normalize(),
      } );
    }

    return this.sticky_mine_targets.length;
  }

  public launchStickyMine( index: number, settings: StickyMineSettings ): boolean
  {
    const target = this.sticky_mine_targets[index];
    if ( !target || !target.entity.isAlive() || SimulationContext.enabled )
      return false;

    const cube_width = PhysicsCube.getSize().x;
    this.sticky_mines.push( new StickyMine(
      target,
      settings.fuse_seconds,
      cube_width,
      ( position, normal ) => this.explodeStickyMine( position, normal, settings )
    ) );
    void audioController.playSound( AudioClips.ROCKET_LAUNCH, false, 0.5 );
    return true;
  }

  public debugLaunchStickyMines(): boolean
  {
    return this.booster_manager.activateForDebug( CumulativeBoosterID.Mine );
  }

  public getActiveStickyMineCount(): number
  {
    return this.sticky_mines.length;
  }

  public clearStickyMines(): void
  {
    for ( const mine of this.sticky_mines )
      mine.dispose();
    this.sticky_mines = [];
    this.sticky_mine_targets = [];
  }

  private updateStickyMines( dt: number ): void
  {
    if ( this.sticky_mines.length === 0 )
      return;

    for ( const mine of this.sticky_mines )
      mine.update( dt );
    this.sticky_mines = this.sticky_mines.filter( mine => !mine.isFinished );
  }

  /** The blast only pushes: blocks are never damaged by it, they fly off or fall. */
  private explodeStickyMine( position: THREE.Vector3, normal: THREE.Vector3, settings: StickyMineSettings ): void
  {
    Vfx3DManager.addFireworkExplosion( position );
    void audioController.playSound( AudioClips.ROCKET_HIT, false, 0.6 );
    void hapticsController.vibrate( HapticPattern.MEDIUM, VibrationContext.GENERAL );

    const cube_size = PhysicsCube.getSize();
    const radius = settings.push_radius * cube_size.x;
    const direction = new THREE.Vector3();
    const inertia_scale = ( cube_size.x * cube_size.x + cube_size.y * cube_size.y ) / 12;

    for ( const entity of this.entities )
    {
      if ( !entity.isAlive() || !GameScreen.hasBody( entity ) || !entity.body.isDynamic() )
        continue;

      const p = entity.body.translation();
      direction.set( p.x - position.x, p.y - position.y, p.z - position.z );
      const distance = direction.length();
      if ( distance > radius )
        continue;

      if ( distance < 1e-4 )
        direction.copy( normal ).negate();
      else
        direction.divideScalar( distance );

      // Mostly outwards, with a lift so the pushed blocks tumble instead of sliding.
      direction.y += 0.35;
      direction.normalize();

      const falloff = Math.sqrt( 1 - distance / radius );
      const mass = entity.body.mass();
      const delta_v = settings.push_speed * ( 0.35 + 0.65 * falloff );
      entity.body.applyImpulse( {
        x: direction.x * mass * delta_v,
        y: direction.y * mass * delta_v,
        z: direction.z * mass * delta_v,
      }, true );

      const spin = 5 * falloff * mass * inertia_scale;
      entity.body.applyTorqueImpulse( {
        x: ( Math.random() - 0.5 ) * 2 * spin,
        y: ( Math.random() - 0.5 ) * 2 * spin,
        z: ( Math.random() - 0.5 ) * 2 * spin,
      }, true );
    }

    this.loss_settle_timer = 0;
  }

  /** Breaks and removes every block in the rocket's 3x3x3 grid area immediately. */
  private breakFireworkArea(
    primary: LevelEntity,
    impact_point: THREE.Vector3
  ): void
  {
    if ( !GameScreen.hasBody( primary ) )
      return;

    const table_index = this.block_table_index.get( primary ) ?? 0;
    const table = this.tables[table_index];
    const cube_size = PhysicsCube.getSize();
    const step_x = cube_size.x * ( 1 + PhysicsCube.GRID_GAP_RATIO );
    const step_z = cube_size.z * ( 1 + PhysicsCube.GRID_GAP_RATIO );
    const targets: { entity: LevelEntity; point: THREE.Vector3 }[] = [];
    const primary_placement = this.block_grid_placement.get( primary );

    if ( primary_placement && primary_placement.cells.length > 0 )
    {
      // Determine which cell of a multi-cell piece was actually struck. The
      // contact is transformed into the body's current local frame, while the
      // chosen neighbours still come from immutable authored grid positions.
      const body_position = primary.body.translation();
      const body_rotation = primary.body.rotation();
      const local_impact = impact_point.clone()
        .sub( new THREE.Vector3( body_position.x, body_position.y, body_position.z ) )
        .applyQuaternion( new THREE.Quaternion(
          body_rotation.x,
          body_rotation.y,
          body_rotation.z,
          body_rotation.w
        ).invert() );
      const centre_col = primary_placement.cells.reduce( ( sum, cell ) => sum + cell.col, 0 )
        / primary_placement.cells.length;
      const centre_row = primary_placement.cells.reduce( ( sum, cell ) => sum + cell.row, 0 )
        / primary_placement.cells.length;
      let hit_cell = primary_placement.cells[0];
      let nearest_distance_sq = Infinity;

      for ( const cell of primary_placement.cells )
      {
        const dx = local_impact.x - ( cell.col - centre_col ) * step_x;
        const dy = local_impact.y - ( cell.row - centre_row ) * cube_size.y;
        const distance_sq = dx * dx + dy * dy;
        if ( distance_sq < nearest_distance_sq )
        {
          hit_cell = cell;
          nearest_distance_sq = distance_sq;
        }
      }

      for ( const entity of this.entities )
      {
        if ( !entity.isAlive() || !GameScreen.hasBody( entity ) )
          continue;

        const placement = this.block_grid_placement.get( entity );
        if (
          !placement
          || placement.table_index !== table_index
          || Math.abs( placement.layer - primary_placement.layer ) > 1.05
          || !placement.cells.some( cell =>
            Math.abs( cell.col - hit_cell.col ) <= 1.05
            && Math.abs( cell.row - hit_cell.row ) <= 1.05
          )
        )
          continue;

        const position = entity.body.translation();
        targets.push( {
          entity,
          point: new THREE.Vector3( position.x, position.y, position.z )
        } );
      }
    }
    else
    {
      // Procedural legacy towers do not have authored grid coordinates.
      const primary_local = table.toUnrotatedFrame(
        primary.body.translation(),
        new THREE.Vector3()
      );

      for ( const entity of this.entities )
      {
        if (
          !entity.isAlive()
          || !GameScreen.hasBody( entity )
          || ( this.block_table_index.get( entity ) ?? 0 ) !== table_index
        )
          continue;

        const world_position = entity.body.translation();
        const local_position = table.toUnrotatedFrame( world_position, new THREE.Vector3() );

        if (
          Math.abs( local_position.x - primary_local.x ) > step_x * 1.05
          || Math.abs( local_position.y - primary_local.y ) > cube_size.y * 1.05
          || Math.abs( local_position.z - primary_local.z ) > step_z * 1.05
        )
          continue;

        targets.push( {
          entity,
          point: new THREE.Vector3( world_position.x, world_position.y, world_position.z )
        } );
      }
    }

    // Stop the solver impulse first so the whole area breaks in place.
    for ( const target of targets )
    {
      target.entity.body.setLinvel( { x: 0, y: 0, z: 0 }, true );
      target.entity.body.setAngvel( { x: 0, y: 0, z: 0 }, true );
    }

    for ( const target of targets )
      target.entity.onFireworkHit(
        target.entity === primary ? impact_point : target.point
      );

    // Remove original meshes in this frame; their shatter VFX remains.
    const destroyed = new Set<LevelEntity>();
    for ( const target of targets )
    {
      // A watermelon owns a two-stage destruction lifecycle and must remain in
      // the level after its first slice is detached.
      if ( target.entity instanceof WatermelonBlock )
        continue;

      destroyed.add( target.entity );
      this.by_collider.delete( target.entity.collider.handle );
      target.entity.dispose();
    }
    this.entities = this.entities.filter( entity => !destroyed.has( entity ) );
  }

  public getBoosterCount( id: CumulativeBoosterID ): number
  {
    return this.booster_manager.getCount( id );
  }

  public getBoosterEffectValue( id: CumulativeBoosterID ): number | null
  {
    return this.booster_manager.getEffectValue( id );
  }

  public addBoosterUses( id: CumulativeBoosterID, amount: number ): void
  {
    this.booster_manager.addUses( id, amount );
  }

  public startInfiniteBallsCountdown(): boolean
  {
    const started = this.booster_manager.startInfiniteBallsCountdown();
    if ( started )
    {
      if ( !this.infinite_balls_music_muted )
      {
        this.infinite_balls_music_muted = true;
        audioController.muteMusicTemporarily();
      }
      void audioController.playSound( AudioClips.INFINITE_SHOTS_MUSIC );
    }
    return started;
  }

  public cancelBoosterAndRefund( id: CumulativeBoosterID ): boolean
  {
    return this.booster_manager.cancelAndRefund( id );
  }

  public canActivateBooster( id: CumulativeBoosterID ): boolean
  {
    if ( this.is_finished )
      return false;

    if ( CumulativeBoosterService.getActivationPhase( id ) === "level_start" )
      return !this.input_blocked_by_tutorial;

    if ( this.input_blocked_by_level_start_booster )
      return false;

    switch ( id )
    {
      case CumulativeBoosterID.BigBall:
        return this.shots_left !== 0 || this.infinite_balls_active;
      case CumulativeBoosterID.InfiniteBalls:
        return true;
      case CumulativeBoosterID.Multiball:
      case CumulativeBoosterID.Fireworks:
        return this.shots_left !== 0;
    }

    return false;
  }

  public activateBooster( id: CumulativeBoosterID ): boolean
  {
    const activated = this.booster_manager.activate( id );
    if ( activated )
    {
      this.booster_used_this_attempt = true;
    }
    return activated;
  }

  public async afterFirstPaint(): Promise<void>
  {
    if ( this.defer_level_start_booster )
      return;

    await this.activatePendingLevelStartBooster();
  }

  public async afterIntroOverlay(): Promise<void>
  {
    this.defer_level_start_booster = false;
    await this.activatePendingLevelStartBooster();
  }

  private async activatePendingLevelStartBooster(): Promise<void>
  {
    const booster_id = this.level_start_booster_id;
    if ( booster_id === null || this.is_finished )
      return;

    this.level_start_booster_id = null;
    this.input_blocked_by_level_start_booster = true;
    try
    {
      await this.booster_manager.activateLevelStartBooster( booster_id );
    }
    finally
    {
      this.input_blocked_by_level_start_booster = false;
    }
  }

  public getAttemptNumber(): number
  {
    return this.attempt_number;
  }

  public shouldHighlightBoosters(): boolean
  {
    if ( this.is_finished || this.attempt_completed || this.booster_used_this_attempt )
      return false;

    const config = AppConfigService.get().booster_highlight;
    if ( this.attempt_number < config.start_attempt )
      return false;

    if ( this.analytics_shots_fired >= config.shots_limit )
      return false;

    const past_start = this.attempt_number - config.start_attempt;
    return past_start % config.frequency === 0;
  }

  public static resetConsecutiveAttempts(): void
  {
    localStorage.removeItem( "booster_highlight_consecutive_level" );
    localStorage.removeItem( "booster_highlight_consecutive_attempts" );
  }

  public setMultiballActive( active: boolean ): void
  {
    this.multiball_active = active;
  }

  public fireMultiballAt(
    target: MultiballTarget,
    mass_multiplier: number,
    yaw_offset_radians: number
  ): void
  {
    this.cannon.fireAtWorldPoint(
      new THREE.Vector3( target.x, target.y, target.z ),
      1,
      mass_multiplier,
      false,
      yaw_offset_radians
    );
    if ( !this.fireworks_active )
      void audioController.playSound( AudioClips.TEN_SHOTS_SINGLE );
  }

  private pointerEventAt( client_x: number, client_y: number ): PointerEvent
  {
    return { clientX: client_x, clientY: client_y } as PointerEvent;
  }

  public beginBoosterAim( client_x: number, client_y: number ): boolean
  {
    if ( this.is_finished
      || this.input_blocked_by_tutorial
      || this.input_blocked_by_level_start_booster
      || Game.instance.isLoadingGameScreenShown() )
    {
      return false;
    }

    this.aiming = true;
    this.cannon.aim( this.pointerEventAt( client_x, client_y ) );
    return true;
  }

  public moveBoosterAim( client_x: number, client_y: number ): void
  {
    if ( this.aiming )
      this.cannon.aim( this.pointerEventAt( client_x, client_y ) );
  }

  public finishBoosterAim( client_x: number, client_y: number ): boolean
  {
    return this.finishAim( this.pointerEventAt( client_x, client_y ) );
  }

  public cancelBoosterAim(): void
  {
    this.aiming = false;
  }

  private collectHighlightedObjects(): Set<THREE.Object3D>
  {
    const highlighted = new Set<THREE.Object3D>();
    const collect = ( root: THREE.Object3D ) => root.traverse( object => highlighted.add( object ) );

    for ( const table of this.tables )
      collect( table.getHighlightObject() );
    for ( const entity of this.entities )
      collect( entity.mesh );
    if ( this.cannon )
    {
      collect( this.cannon.getHighlightObject() );
      for ( const ball of this.cannon.getBalls() )
        collect( ball.mesh );
    }

    return highlighted;
  }

  public setBoosterObjectHighlightEnabled( enabled: boolean ): void
  {
    if ( !enabled )
    {
      SceneDimmer.disable();
      return;
    }

    const highlighted = this.collectHighlightedObjects();
    const background_roots: THREE.Object3D[] = [];

    SceneManager.scene.traverse( object =>
    {
      if ( !( object as THREE.Mesh ).isMesh || highlighted.has( object ) )
        return;

      background_roots.push( object );
    } );

    SceneDimmer.enable(
      SceneManager.scene,
      background_roots,
      BoosterHighlightRenderer.BACKGROUND_OPACITY
    );
  }

  public setBlockedTapHitTest( fn: ( client_x: number, client_y: number ) => boolean ): void
  {
    this.blocked_tap_hit_test = fn;
  }

  public setTutorialInputBlocked( blocked: boolean ): void
  {
    this.input_blocked_by_tutorial = blocked;
  }

  // [SIM] викликати ДО init()
  public enableSimulation(
    on_finished: ( r: SimulationRunResult ) => void,
    spawn_bot = true
  ): void
  {
    this.simulation = true;
    this.on_simulation_finished = on_finished;
    if ( spawn_bot )
      this.bot = new GameBot( this );
  }

  public isFinished(): boolean
  {
    return this.is_finished;
  }

  public isChallenge(): boolean
  {
    return this.is_challenge;
  }

  public async init( {
    level,
    is_challenge,
    level_start_booster_id,
    defer_level_start_booster,
  }: {
    level: number;
    is_challenge?: boolean;
    level_start_booster_id?: CumulativeBoosterID;
    defer_level_start_booster?: boolean;
  } ): Promise<void>
  {
    console.log( "GameScreen init" );

    this.is_challenge = is_challenge ?? false;
    this.level_start_booster_id = level_start_booster_id ?? null;
    this.defer_level_start_booster = defer_level_start_booster ?? false;

    if ( !StressTest.isStressLevel( level ) )
      StressTest.deactivate();

    if ( BlockShowcase.isShowcaseLevel( level ) )
      BlockShowcase.beginLevel();
    else
      BlockShowcase.deactivate();

    if ( TextureTest.isTextureLevel( level ) )
      TextureTest.beginLevel( level );
    else
      TextureTest.deactivate();

    BarrierShowcase.beginLevel( level );

    if ( TableFormationShowcase.isShowcaseLevel( level ) )
      TableFormationShowcase.beginLevel( level );
    else
      TableFormationShowcase.deactivate();

    Vfx3DRuntime.registerAll();
    this.level = level;
    if ( !this.simulation && !GameScreen.isDebugRun() )
    {
      const stored_level = Number( localStorage.getItem( "booster_highlight_consecutive_level" ) );
      let attempts = Number( localStorage.getItem( "booster_highlight_consecutive_attempts" ) );

      if ( stored_level === this.level )
      {
        attempts++;
      }
      else
      {
        localStorage.setItem( "booster_highlight_consecutive_level", this.level.toString() );
        attempts = 1;
      }
      localStorage.setItem( "booster_highlight_consecutive_attempts", attempts.toString() );
      this.attempt_number = attempts;
      recordLevelAttempt( this.level );
    }
    else
    {
      this.attempt_number = 1;
    }

    const challenge_level = this.is_challenge ? DailyChallengeService.getLoadedLevel( this.level ) : null;
    const difficulty = this.is_challenge
      ? ( challenge_level?.config.difficulty ?? PlayDialogDifficulty.SuperHard )
      : getLevelDifficulty( this.level );

    this.onDifficultyChanged( difficulty, this.level );

    this.shots_left = GameScreen.SHOTS_BY_DIFFICULTY[difficulty];

    // The balance curve of a server release assigns each level its own shots
    // budget — it beats the per-tier defaults when present. A daily challenge
    // stage carries its own budget instead and must not read the campaign's.
    if ( challenge_level )
    {
      if ( challenge_level.shots !== null && challenge_level.shots > 0 )
        this.shots_left = challenge_level.shots;
    }
    else if ( !this.is_challenge )
    {
      const pack_shots = LevelPackService.getShots( this.level );
      if ( pack_shots !== null )
        this.shots_left = pack_shots;
    }

    // Segment mass tuning can differ per level range; blocks read PhysicsMass
    // at construction, so set the multiplier before the level is built. The
    // editor's simulation and playtest stay on base masses (×1) so their
    // verdicts don't depend on whichever segment the dev machine lands in.
    const level_mass = LevelPackService.getMassModifierFor( this.level );
    let attempt_mass_multiplier = 1;
    let attempt_mass_source = "not-requested";
    let attempt_mass_range = "";
    let server_attempt_number: number | null = null;
    // The challenge never charges hearts, so it must not open a server-side
    // attempt either — a lingering level_started_at marker would be settled
    // as an abandoned attempt later and silently eat a heart.
    if ( !this.simulation && !GameScreen.isDebugRun() && !this.is_challenge )
    {
      try
      {
        const start = await reportLevelStart( this.level );
        if ( Number.isFinite( start.attempt_mass_multiplier ) && start.attempt_mass_multiplier > 0 )
          attempt_mass_multiplier = start.attempt_mass_multiplier;
        attempt_mass_source = start.attempt_mass?.source ?? "server";
        if ( Number.isInteger( start.attempt_number ) && start.attempt_number > 0 )
          server_attempt_number = start.attempt_number;
        if ( start.attempt_mass?.range )
          attempt_mass_range = ` range=${start.attempt_mass.range.from}-${start.attempt_mass.range.to}`;
      }
      catch ( error )
      {
        console.warn( "[LevelMass] level-start failed; using level-pack mass only", error );
      }
    }
    const requested_mass_multiplier = level_mass.multiplier * attempt_mass_multiplier;
    const applied_mass_multiplier = this.simulation || GameScreen.isDebugRun()
      ? setBlockMassMultiplier( 1 )
      : setBlockMassMultiplier( requested_mass_multiplier );
    this.analytics_mass = {
      [AnalyticsPropertyKey.ATTEMPT_NUMBER]: server_attempt_number ?? this.attempt_number,
      [AnalyticsPropertyKey.MASS_ATTEMPT]: attempt_mass_multiplier,
      [AnalyticsPropertyKey.MASS_LEVEL]: level_mass.multiplier,
      [AnalyticsPropertyKey.MASS_APPLIED]: applied_mass_multiplier,
      [AnalyticsPropertyKey.MASS_SOURCE]: attempt_mass_source,
    };

    // Editor playtest (?test_level=) is for inspecting the level, not for
    // economy — shoot as much as you like, same as the simulation bot.
    if ( this.simulation || GameScreen.isDebugRun() )
      this.shots_left = 1_000_000;

    this.onShotsLeftChanged( this.shots_left );

    if ( !this.simulation )
      this.addListeners();

    if ( !this.simulation )
      this.createLights();

    const level_config = this.is_challenge
      ? ( DailyChallengeService.getLoadedLevel( this.level )?.config
          ?? { level: this.level, difficulty: PlayDialogDifficulty.SuperHard } )
      : getLevelConfig( this.level );
    this.analytics_level_uid = typeof level_config.uid === "string" ? level_config.uid : "";

    this.createCamera();

    this.createWorld( level_config );

    this.createCannon();

    if ( level_config.tables?.length )
    {
      const tables = level_config.tables.slice( 0, MAX_TABLES );
      const cols_limit = maxTableGridCols();
      tables.forEach( ( table, index ) =>
        this.createLayeredBlocks( table.layers, index, cols_limit, MAX_TABLE_LAYERS, this.shelfLimits( index ) ) );
    }
    this.debug_empty_level = GlobalSettings.DEBUG_EMPTY_LEVEL;
    GlobalSettings.DEBUG_EMPTY_LEVEL = false;

    if ( this.debug_empty_level )
      this.shots_left = 1_000_000;
    else if ( level_config.tables?.length )
      {}
    else if ( level_config.layers && level_config.layers.length > 0 )
      this.createLayeredBlocks( level_config.layers, 0 );
    else if ( areBundledLevelsAllowed() || GameScreen.isDebugRun() )
      this.createRandomTowers();
    else
      console.error( `[GameScreen] level ${this.level} arrived without a server layout — building nothing instead of a generated one` );

    if ( level_config.tables?.length )
    {
      if ( level_config.barriers?.length )
        console.warn( `[GameScreen] level ${this.level}: root barriers are ignored on a level with tables — put them inside tables[]` );

      level_config.tables.slice( 0, MAX_TABLES ).forEach( ( table, index ) =>
      {
        if ( table.barriers?.length )
          this.createBarriers( table.barriers, index );
      } );
    }
    else if ( level_config.barriers?.length )
      this.createBarriers( level_config.barriers, 0 );

    this.dropNestedEdgePads();

    if ( TextureTest.isActive() )
      TextureTest.onLevelBuilt( () => this.entities );

    const total_level_mass = this.entities.reduce( ( total, entity ) => total + entity.collider.mass(), 0 );
    const level_range = level_mass.range ? ` range=${level_mass.range.from}-${level_mass.range.to}` : "";
    console.info(
      `[LevelMass] level=${this.level} mass=${total_level_mass.toFixed( 2 )} ` +
      `level_pack=x${level_mass.multiplier} (${level_mass.source}${level_range}) ` +
      `attempt=x${attempt_mass_multiplier} (${attempt_mass_source}${attempt_mass_range}) ` +
      `applied=x${applied_mass_multiplier}`
    );

    level_config.tables?.slice( 0, MAX_TABLES ).forEach( ( table, index ) =>
    {
      if ( table.rotation?.angle )
        this.setFieldRotation( table.rotation.angle, false, index );
      if ( table.rotation?.spin )
        this.startFieldSpin( table.rotation.spin, index );
    } );

    this.analytics_game_id = generateRandomId();
    this.analytics_game_started_at = performance.now();
    this.analytics_initial_cube_count = this.countScoreTargets();
    this.analytics_game_number = nextGameNumber();
    this.analytics_start_moves = this.shots_left;
    this.analytics_shots_fired = 0;
    this.booster_used_this_attempt = false;
    this.analytics_moves_purchased_times = 0;
    this.booster_manager.resetUsedCounts();
    this.analytics_finish_tracked = false;
    this.heart_settlement = null;
    this.attempt_completed = false;
    this.loss_settle_timer = 0;

    if ( !this.simulation && !GameScreen.isDebugRun() )
    {
      const mode =
        level_config.difficulty === PlayDialogDifficulty.SuperHard ? 'super_hard'
        : level_config.difficulty === PlayDialogDifficulty.Hard ? 'hard'
        : 'common';
      void logGameStart( {
        mode,
        level: this.level,
        session_id: AnalyticStore.getSessionId(),
      } );
      // The challenge never charges hearts, so it must not open a server-side
      // attempt either — a lingering level_started_at marker would be settled
      // as an abandoned attempt later and silently eat a heart.
      if ( !this.is_challenge )
        void reportLevelStart( this.level )
          .then( () => OfferTriggerController.report( 'level_started', { level: this.level } ) )
          .catch( e => console.warn( '[Economy] level-start failed', e ) );
      analytics.track( AnalyticsEventName.GAME_START, {
        [AnalyticsPropertyKey.USERLEVEL]: this.level,
        [AnalyticsPropertyKey.GAMETYPE]: AnalyticsGameType.ROYAL_SMASH,
        [AnalyticsPropertyKey.GAME_NUMBER]: this.analytics_game_number,
        [AnalyticsPropertyKey.LEVEL_ID]: String( this.level ),
        [AnalyticsPropertyKey.DIFFICULTY]: GameScreen.toAnalyticsDifficulty( difficulty ),
        [AnalyticsPropertyKey.START_MOVES]: this.analytics_start_moves,
        [AnalyticsPropertyKey.LEVEL_UID]: this.analytics_level_uid,
        [AnalyticsPropertyKey.GAMEID]: this.analytics_game_id,
        [AnalyticsPropertyKey.CELLS]: this.analytics_initial_cube_count,
        ...this.analytics_mass,
        ...balanceSnapshot(),
      } );
    }

    if ( !this.simulation )
      void audioController.playSound( AudioClips.START_LEVEL );

    this.fitShadowCamera();
    applyPixelFeatures();

    if ( !this.simulation )
      this.warmupShaders();

    this.level_built_pending = true;
  }

  // Shader programs are built the first time a material is drawn. Debris
  // materials only appear when a block breaks, so that compile used to land
  // mid-gameplay as a ~700ms freeze. Compiling a stand-in up front moves the
  // cost into level load, where it is invisible.
  private warmupShaders(): void
  {
    if ( GameScreen.shaders_warmed )
      return;
    GameScreen.shaders_warmed = true;

    const probes: THREE.Mesh[] = [
      VfxBlockShatterTask.createWarmupProbe(),
      CannonBall.createWarmupProbe(),
      IceBlock.createWarmupProbe(),
      VfxJamSpillTask.createWarmupProbe(),
    ];

    for ( const probe of probes )
    {
      probe.position.copy( SceneManager.camera.position );
      SceneManager.world.add( probe );

      const materials = Array.isArray( probe.material ) ? probe.material : [ probe.material ];
      GameScreen.warmup_materials.push( ...materials );
    }

    Renderer.renderer.compile( SceneManager.scene, SceneManager.camera );

    for ( const probe of probes )
    {
      SceneManager.world.remove( probe );
      probe.geometry.dispose();
      // NOTE: We deliberately do NOT call material.dispose() here!
      // In Three.js, disposing the material deallocates the program from WebGLPrograms
      // and triggers gl.deleteProgram(), which destroys the newly compiled shader on the GPU
      // and causes GL_INVALID_VALUE / mid-gameplay re-compilation freezes.
    }
  }

  private dropNestedEdgePads(): void
  {
    const world = PhysicsWorld.getWorld();
    const nested = this.entities.filter( ( entity ): entity is LevelEntity & BreakablePhysicsEntity =>
      entity instanceof BreakablePhysicsEntity && entity.hasExtraColliders() && restsOnFaces( entity.collider, world ) );

    for ( const entity of nested )
      entity.removeExtraColliders();
  }

  private addEntity( e: LevelEntity, table_index = 0 ): void
  {
    this.block_table_index.set( e, table_index );
    this.entities.push( e );
    this.by_collider.set( e.collider.handle, e );
  }

  /**
   * Puts the level's barriers in the air in FRONT of the table, out of the
   * grid the blocks live in: `x` runs across the frame from the middle of the
   * table, `y` up from the tabletop, `z` forwards from the table's front edge
   * — all in grid cells, so a barrier keeps its size relative to the blocks.
   * The mount then reaches out of the frame: up past the top for a pillar out
   * of the sky, or out past the left / right edge for a rod from the side.
   */
  private createBarriers( configs: BarrierConfig[], table_index: number ): void
  {
    const table = this.tables[table_index];
    if ( !table )
      return;

    const cube = PhysicsCube.getSize();
    const center = table.getCenter();
    const front_z = center.z + table.getSize().z * 0.5;
    const table_top = table.getTopY();

    for ( const config of configs )
    {
      const head = ( config.head ?? BarrierHead.Blade ) as BarrierHead;
      const mount = ( config.mount ?? BarrierMount.Ceiling ) as BarrierMount;
      const bearing = new THREE.Vector3(
        center.x + config.x * cube.x,
        table_top + config.y * cube.y,
        front_z + ( config.z ?? 1 ) * cube.z
      );

      // Every mount only has to leave the frame, and the camera is already
      // fitted by the time barriers are built. A sky pillar gets a fixed drop —
      // nothing above the frame is ever seen. A side rod is measured to the
      // frame's edge at the barrier's own depth, plus a little overshoot, so it
      // always disappears off the side however wide the formation made the shot.
      let mount_length = GameScreen.BARRIER_SKY_DROP_CELLS * cube.y;

      if ( mount === BarrierMount.Left || mount === BarrierMount.Right )
      {
        const camera = this.camera_controller.getCamera();
        const half_width =
          Math.tan( THREE.MathUtils.degToRad( camera.fov * 0.5 ) )
          * Math.max( camera.position.z - bearing.z, 0 )
          * camera.aspect;
        const edge_x = mount === BarrierMount.Left
          ? camera.position.x - half_width
          : camera.position.x + half_width;

        mount_length = Math.abs( bearing.x - edge_x ) + GameScreen.BARRIER_SIDE_OVERSHOOT_CELLS * cube.x;
      }

      const barrier = new Barrier(
        head,
        mount,
        bearing,
        mount_length,
        ( config.angle ?? 0 ) * Math.PI / 180
      );

      this.barriers.push( barrier );

      // Barriers stop the aim line the way the table does — they are solid,
      // so a shot that ends on one is a real shot and not a miss.
      for ( const handle of barrier.getBodyHandles() )
        this.aim_body_handles.add( handle );
    }
  }

  private static isScoreTarget( kind: EntityKind ): boolean
  {
    return kind === EntityKind.Cube || kind === EntityKind.Metal || kind === EntityKind.Ice;
  }

  private countScoreTargets(): number
  {
    let n = 0;
    for ( const e of this.entities )
      if ( GameScreen.isScoreTarget( e.kind ) )
        n++;
    return n;
  }

  private addListeners(): void
  {
    window.addEventListener(
      "pointerdown",
      this.onPointerDown
    );

    window.addEventListener(
      "pointermove",
      this.onPointerMove
    );

    window.addEventListener(
      "pointerup",
      this.onPointerUp
    );
  }

  private static readonly SHADOW_MAP_SIZE = 1024;
  private static readonly SHADOW_FIT_MARGIN = 3;
  private static readonly SHADOW_RECEIVER_MARGIN = 2;

  // The projection is fitted to what the level actually contains instead of a
  // fixed 28x28 box: the same map then spends all of its texels on the play
  // area, so a quarter-size map still resolves finer contact detail.
  private fitShadowCamera(): void
  {
    if ( !this.sun )
      return;

    SceneManager.world.updateMatrixWorld( true );
    this.sun.updateMatrixWorld( true );
    this.sun.target.updateMatrixWorld( true );
    this.sun.shadow.updateMatrices( this.sun );

    const camera = this.sun.shadow.camera;
    const to_light = camera.matrixWorldInverse;
    const corner = new THREE.Vector3();
    const ground_point = new THREE.Vector3();
    const box = new THREE.Box3();

    const light_direction = this.sun.target.getWorldPosition( new THREE.Vector3() )
      .sub( this.sun.getWorldPosition( new THREE.Vector3() ) )
      .normalize();
    const can_project = Math.abs( light_direction.y ) > 1e-4;
    const ground = { min_x: Infinity, max_x: -Infinity, min_z: Infinity, max_z: -Infinity };

    let min_x = Infinity, max_x = -Infinity;
    let min_y = Infinity, max_y = -Infinity;
    let min_depth = Infinity, max_depth = -Infinity;
    let found = false;

    SceneManager.world.traverse( object =>
    {
      const mesh = object as THREE.Mesh;
      if ( !mesh.isMesh || !mesh.castShadow || !mesh.geometry )
        return;

      if ( !mesh.geometry.boundingBox )
        mesh.geometry.computeBoundingBox();

      box.copy( mesh.geometry.boundingBox! ).applyMatrix4( mesh.matrixWorld );
      found = true;

      for ( let i = 0; i < 8; i++ )
      {
        corner.set(
          i & 1 ? box.max.x : box.min.x,
          i & 2 ? box.max.y : box.min.y,
          i & 4 ? box.max.z : box.min.z
        );

        if ( can_project )
        {
          ground_point.copy( corner ).addScaledVector( light_direction, -corner.y / light_direction.y );
          ground.min_x = Math.min( ground.min_x, ground_point.x );
          ground.max_x = Math.max( ground.max_x, ground_point.x );
          ground.min_z = Math.min( ground.min_z, ground_point.z );
          ground.max_z = Math.max( ground.max_z, ground_point.z );
        }

        corner.applyMatrix4( to_light );

        min_x = Math.min( min_x, corner.x );
        max_x = Math.max( max_x, corner.x );
        min_y = Math.min( min_y, corner.y );
        max_y = Math.max( max_y, corner.y );
        min_depth = Math.min( min_depth, -corner.z );
        max_depth = Math.max( max_depth, -corner.z );
      }
    } );

    if ( !found )
      return;

    const margin = GameScreen.SHADOW_FIT_MARGIN;

    camera.left = min_x - margin;
    camera.right = max_x + margin;
    camera.bottom = min_y - margin;
    camera.top = max_y + margin;
    camera.near = Math.max( 0.5, min_depth - margin );
    camera.far = max_depth + margin;
    camera.updateProjectionMatrix();
    camera.updateMatrixWorld( true );

    if ( can_project )
      this.fitShadowReceiver( ground );

    Renderer.requestShadowUpdate();
  }

  private fitShadowReceiver( bounds: { min_x: number; max_x: number; min_z: number; max_z: number } ): void
  {
    const margin = GameScreen.SHADOW_RECEIVER_MARGIN;

    this.ground.setShadowArea(
      bounds.min_x - margin,
      bounds.max_x + margin,
      bounds.min_z - margin,
      bounds.max_z + margin
    );
  }

  private createLights(): void
  {
    const no_sun = GlobalSettings.DEBUG_NO_FRONT_LIGHT;

    const sun = new THREE.DirectionalLight(
      0xfff6e8,
      5
    );
    sun.visible = !no_sun;
    this.sun = sun;
    sun.position.set(1.5, 5, 4);
    sun.castShadow = true;
    sun.shadow.intensity = 0.45;
    
    sun.shadow.mapSize.width = GameScreen.SHADOW_MAP_SIZE;
    sun.shadow.mapSize.height = GameScreen.SHADOW_MAP_SIZE;
    
    const rim = new THREE.DirectionalLight(0x88bbff, 1);
    rim.name = "rim";
    rim.position.set(-5, 2, -4);
    rim.castShadow = false;

    const hemi = new THREE.HemisphereLight( 0xdff1ff, 0x2a3f6b, 0.3 );
    hemi.name = "hemi";
    
    // const rim2 = new THREE.DirectionalLight(0x88bbff, 2);
    // rim2.position.set(5, 2, -4);
    // rim2.castShadow = false;
    
    const ambient = new THREE.AmbientLight(0x888888, 0.5);
    
    // Keep the shadow projection close to the playable area. The old 40x40
    // projection spread the same map over too much empty space and washed out
    // contact detail between stacked blocks.
    // Keep every shadow-frustum edge outside the visible play area. When the
    // lower edge crossed the scene it produced a screen-wide horizontal seam:
    // objects above it received shadows while objects below it abruptly did
    // not.
    sun.shadow.camera.left = -14;
    sun.shadow.camera.right = 14;
    sun.shadow.camera.top = 16;
    sun.shadow.camera.bottom = -12;
    
    sun.shadow.camera.near = 1;
    sun.shadow.camera.far = 50;
    sun.shadow.bias = -0.0002;
    sun.shadow.normalBias = 0.012;
    sun.shadow.radius = 2;
    
    SceneManager.world.add( sun );
    SceneManager.world.add( rim );
    SceneManager.world.add( hemi );
    SceneManager.world.add( ambient );
    // SceneManager.world.add( rim2 );

    this.applyGraphicsProfile( GraphicsSettings.get(), GraphicsSettings.profile() );
    GraphicsSettings.subscribe( this.graphics_listener );
    RimLightTest.reapply();
  }

  private readonly graphics_listener = ( quality: GraphicsQuality, profile: GraphicsProfile ): void =>
  {
    this.applyGraphicsProfile( quality, profile );
  };

  private applyGraphicsProfile( _quality: GraphicsQuality, profile: GraphicsProfile ): void
  {
    CannonBall.cast_shadow = profile.ball_shadow && profile.shadows !== "off";
    GraphicsApply.applyScene( SceneManager.world, profile );
    Renderer.requestShadowUpdate();
  }
  
  private static readonly TILT_AXIS = new THREE.Vector3( 0, 0, 1 );

  private static tiltRad( tilt_deg?: number ): number
  {
    return tilt_deg !== undefined && Number.isFinite( tilt_deg )
      ? tilt_deg * Math.PI / 180
      : 0;
  }

  private tables_carrying: ( number | undefined )[] = [];

  private shelfLimits( table_index: number ): ShelfLimits | undefined
  {
    const above = this.tables_carrying[table_index];

    if ( above === undefined )
      return undefined;

    const shelf = this.tables[above];
    const center = shelf.getTopCenter();
    const leg = shelf.getLegSize();

    // A hanging shelf has no leg coming down through this deck, so nothing
    // here is out of bounds horizontally — only its underside still is. An
    // empty range keeps blockedByShelf()'s overlap test from ever matching.
    const has_leg = leg.x > 0 && leg.z > 0;

    return {
      ceiling_y: shelf.getPlatformBottomY(),
      min_x: has_leg ? center.x - leg.x * 0.5 : Infinity,
      max_x: has_leg ? center.x + leg.x * 0.5 : -Infinity,
      min_z: has_leg ? center.z - leg.z * 0.5 : Infinity,
      max_z: has_leg ? center.z + leg.z * 0.5 : -Infinity,
    };
  }

  /**
   * The piece's authored rotation in radians, or undefined when it carries none.
   *
   * Every block type takes one. It used to reach only Cube and Tree, so a
   * rotationDeg on a jar, a can, an ice block, a cone or a melon was read by the
   * payload parser and then quietly dropped here.
   *
   * Not the same thing as the payload's `lying`, though they look alike: this
   * turns the rigid BODY and leaves the art alone, while `lying` bakes a quarter
   * turn into the MESH before the body is built — so `lying` also turns the
   * collider and the entity's own `size`, and a rotation does not. For a 1x1
   * piece the two land in the same place; for a taller one they do not.
   */
  private static authoredRotation( piece: LevelPiece ): { x?: number; y?: number; z?: number } | undefined
  {
    if ( piece.rotationDegX === undefined
      && piece.rotationDegY === undefined
      && piece.rotationDegZ === undefined )
      return undefined;

    return {
      x: piece.rotationDegX === undefined ? undefined : piece.rotationDegX * Math.PI / 180,
      y: piece.rotationDegY === undefined ? undefined : piece.rotationDegY * Math.PI / 180,
      z: piece.rotationDegZ === undefined ? undefined : piece.rotationDegZ * Math.PI / 180,
    };
  }

  /**
   * The same rotation with the tabletop's own slope folded into Z, for the block
   * types that have always been laid down along the slope rather than left
   * upright on it.
   */
  private static tiltedRotation(
    authored: { x?: number; y?: number; z?: number } | undefined,
    tilt_rad: number
  ): { x?: number; y?: number; z?: number } | undefined
  {
    const z = ( authored?.z ?? 0 ) + tilt_rad;

    if ( authored === undefined && z === 0 )
      return undefined;

    return { x: authored?.x, y: authored?.y, z: z === 0 ? undefined : z };
  }

  private static blockedByShelf(
    shelf: ShelfLimits,
    position: THREE.Vector3,
    piece: LevelPiece,
    cube_size: THREE.Vector3,
    step_x: number,
    step_z: number
  ): boolean
  {
    if ( position.y + piece.height * cube_size.y * 0.5 > shelf.ceiling_y )
      return true;

    const half_x = piece.width * step_x * 0.5;
    const half_z = step_z * 0.5;

    return (
      position.x + half_x > shelf.min_x &&
      position.x - half_x < shelf.max_x &&
      position.z + half_z > shelf.min_z &&
      position.z - half_z < shelf.max_z
    );
  }

  private static readonly SHELF_CLEARANCE_MARGIN = 0.3;

  /**
   * Whether this table's deck slides side to side.
   *
   * A spinning table is refused it: PhysicsTable undoes the spin and the slide
   * in one pass (see toUnrotatedFrame), and for the pair it would read "what is
   * standing on this deck" off the wrong spot — blocks would drop out of the
   * field mid-stroke.
   */
  private static slides( cfg: TableConfig ): boolean
  {
    const spins = !!cfg.rotation && ( ( cfg.rotation.angle ?? 0 ) !== 0 || ( cfg.rotation.spin ?? 0 ) !== 0 );

    if ( cfg.slide && spins )
      console.warn( "[FieldLayout] a table asks for both a spin and a slide — the slide is ignored" );

    return !!cfg.slide && !spins;
  }

  private static shelfClearance(
    base_cfg: TableConfig,
    cols_limit: number,
    cube_size: THREE.Vector3
  ): number
  {
    return towerTopHeight( base_cfg.layers, cols_limit, MAX_TABLE_LAYERS ) * cube_size.y
      + GameScreen.SHELF_CLEARANCE_MARGIN;
  }

  private static standOnIndex( cfg: TableConfig, i: number ): number | undefined
  {
    const on = cfg.standOn;

    if ( on === undefined )
      return undefined;

    if ( !Number.isInteger( on ) || on < 0 || on >= i )
    {
      console.warn( `[FieldLayout] table ${i}: standOn ${on} must name an earlier table — ignored` );
      return undefined;
    }

    return on;
  }

  private static tableLifts( configs: TableConfig[], claims: TableClaim[] ): number[]
  {
    const lifts = configs.map( () => 0 );

    const edgeRise = ( i: number ) =>
      claims[i].target_width * 0.5 * Math.abs( Math.tan( GameScreen.tiltRad( configs[i].tiltDeg ) ) );

    configs.forEach( ( cfg, i ) =>
    {
      const from = cfg.liftFrom;

      if ( from === undefined )
        return;

      if ( !Number.isInteger( from ) || from < 0 || from >= i )
      {
        console.warn( `[FieldLayout] table ${i}: liftFrom ${from} must name an earlier table — ignored` );
        return;
      }

      lifts[i] = lifts[from] + edgeRise( from ) + edgeRise( i );
    } );

    return lifts;
  }

  private createCamera(): void
  {
    this.camera_controller = new CameraController();
    SceneManager.camera = this.camera_controller.getCamera();
  }

  private createWorld( level_config: LevelConfig ): void
  {
    const aspect = SceneManager.camera.aspect;
    const tall_factor = THREE.MathUtils.clamp(
      ( this.TABLE_BASE_ASPECT - aspect )
      / ( this.TABLE_BASE_ASPECT - this.TABLE_TALL_ASPECT ),
      0,
      1
    );
    const wide_factor = THREE.MathUtils.clamp(
      ( aspect - this.TABLE_BASE_ASPECT )
      / ( this.TABLE_WIDE_ASPECT - this.TABLE_BASE_ASPECT ),
      0,
      1
    );
    const edge_factor = Math.max( tall_factor, wide_factor );
    this.table_position.z = THREE.MathUtils.lerp( 2, this.TABLE_EDGE_Z, edge_factor ) - 6.25;

    const backdrop_anchor = this.table_position.clone();
    const table_spot = this.camera_controller.floorPointAt( GameScreen.TABLE_SCREEN_Y, backdrop_anchor.y );

    if ( table_spot )
      this.table_position.copy( table_spot );
    else
      console.warn( `[Camera] the sight line at TABLE_SCREEN_Y ${GameScreen.TABLE_SCREEN_Y} never reaches the floor — aim the camera lower or the tables further down the screen` );

    const cube_size = PhysicsCube.getSize();
    const step_z = cube_size.z * ( 1 + PhysicsCube.GRID_GAP_RATIO );
    const step_x = cube_size.x * ( 1 + PhysicsCube.GRID_GAP_RATIO );

    const table_configs = level_config.tables?.length
      ? level_config.tables.slice( 0, MAX_TABLES )
      : null;
    const table_texture = this.is_challenge
      ? ResourceID.TableRedBaseMap
      : level_config.difficulty === PlayDialogDifficulty.SuperHard
        ? ResourceID.TableRedBaseMap
        : level_config.difficulty === PlayDialogDifficulty.Hard
          ? ResourceID.TableGreyVioletBaseMap
          : ResourceID.TableBlueBaseMap;

    const fit_tables: TableFit[] = [];
    const min_top_size = PhysicsTable.getMinTopSize();
    const min_top = { width: min_top_size.x, depth: min_top_size.z };

    if ( !table_configs )
    {
      const layers = level_config.layers ?? [];

      const claim = tableClaim(
        layers,
        maxTableGridCols(),
        false,
        cube_size,
        GameScreen.tiltRad( level_config.tiltDeg ),
        level_config.topScale,
        level_config.slide ? SLIDE_CONFIG.distance : 0,
        min_top
      );

      fit_tables.push( { layers, claim, cols_limit: Infinity, layer_limit: Infinity } );

      this.ground = new Ground( backdrop_anchor, this.is_challenge, GameScreen.TABLE_SCREEN_Y );

      this.tables = [
        new PhysicsTable(
          this.table_position,
          claim.target_depth,
          claim.target_width,
          table_texture,
          GameScreen.tiltRad( level_config.tiltDeg ),
          0,
          undefined,
          0,
          level_config.hang ? HANG_CONFIG : undefined,
          level_config.shape,
          level_config.elevator ? ELEVATOR_CONFIG : undefined,
          level_config.slide ? SLIDE_CONFIG : undefined
        )
      ];
    }
    else
    {
      const base_top = PhysicsTable.getBaseTopSize();
      const cols_limit = maxTableGridCols();

      const claims: TableClaim[] = table_configs.map( cfg =>
      {
        const claim = tableClaim(
          cfg.layers,
          cols_limit,
          !!cfg.rotation && ( ( cfg.rotation.angle ?? 0 ) !== 0 || ( cfg.rotation.spin ?? 0 ) !== 0 ),
          cube_size,
          GameScreen.tiltRad( cfg.tiltDeg ),
          cfg.topScale,
          GameScreen.slides( cfg ) ? SLIDE_CONFIG.distance : 0,
          min_top
        );

        if ( cfg.shape === "round" )
        {
          const diameter = Math.max( claim.target_width, claim.target_depth );
          claim.target_width = diameter;
          claim.target_depth = diameter;
          claim.half_width = diameter * 0.5;
          claim.half_depth = diameter * 0.5;
          claim.radius = diameter * 0.5;
        }

        return claim;
      } );

      const tp = this.table_position;

      const camera = this.camera_controller.getCamera();
      const frame_half_width =
        cube_size.y * camera.aspect / ( 2 * GameScreen.MIN_CUBE_SCREEN_HEIGHT )
        * GameScreen.LEVEL_FRAME_FILL
        * GameScreen.LAYOUT_BUDGET_SHRINK;

      const layout = chooseFieldLayout(
        this.level,
        claims,
        frame_half_width,
        table_configs.map( cfg => cfg.slot )
      );

      const grounded = layout.placements.flatMap( ( at, i ) =>
      {
        if ( GameScreen.standOnIndex( table_configs[i], i ) !== undefined )
          return [];

        const claim = claims[i];
        const reach_x = claim.rotates ? claim.radius : claim.half_width;
        const reach_z = claim.rotates ? claim.radius : claim.half_depth;

        return [ { min_x: at.x - reach_x, max_x: at.x + reach_x, min_z: at.z - reach_z, max_z: at.z + reach_z } ];
      } );
      const group_x = ( Math.min( ...grounded.map( g => g.min_x ) ) + Math.max( ...grounded.map( g => g.max_x ) ) ) * 0.5;
      const group_z = ( Math.min( ...grounded.map( g => g.min_z ) ) + Math.max( ...grounded.map( g => g.max_z ) ) ) * 0.5;

      const xs = layout.placements.map( p => tp.x + p.x - group_x );
      const zs = layout.placements.map( p => tp.z + p.z - group_z );

      if ( !GlobalSettings.IS_PROD )
      {
        for ( const issue of layoutOverlaps(
          layout,
          claims,
          table_configs.map( ( cfg, i ) => GameScreen.standOnIndex( cfg, i ) )
        ) )
          console.warn( `[FieldLayout] ${issue}` );

        for ( const issue of modelSizeDrift( cube_size, base_top ) )
          console.warn( `[FieldLayout] ${issue}` );

        const at = layout.placements.map( p => `(${p.x.toFixed( 3 )}, ${p.z.toFixed( 3 )})` ).join( " " );
        console.log( `[FieldLayout] level ${this.level}: "${layout.formation_id}" · ${table_configs.length} tables · ${at}` );
      }

      table_configs.forEach( ( cfg, i ) => fit_tables.push( {
        layers: cfg.layers,
        claim: claims[i],
        cols_limit,
        layer_limit: MAX_TABLE_LAYERS,
        rotation: cfg.rotation
      } ) );

      this.ground = new Ground( backdrop_anchor, this.is_challenge, GameScreen.TABLE_SCREEN_Y );

      const lifts = GameScreen.tableLifts( table_configs, claims );

      this.tables = [];
      this.tables_carrying = table_configs.map( () => undefined );

      table_configs.forEach( ( cfg, i ) =>
      {
        const on = GameScreen.standOnIndex( cfg, i );
        const base = on === undefined ? undefined : this.tables[on];

        if ( on !== undefined )
          this.tables_carrying[on] = i;

        this.tables.push( new PhysicsTable(
          new THREE.Vector3( base ? base.getTopCenter().x : xs[i], tp.y, base ? base.getTopCenter().z : zs[i] ),
          claims[i].target_depth,
          claims[i].target_width,
          table_texture,
          GameScreen.tiltRad( cfg.tiltDeg ),
          lifts[i],
          base?.getTopSurfaceY(),
          on === undefined ? undefined : GameScreen.shelfClearance( table_configs[on], cols_limit, cube_size ),
          cfg.hang ? HANG_CONFIG : undefined,
          cfg.shape,
          cfg.elevator ? ELEVATOR_CONFIG : undefined,
          GameScreen.slides( cfg ) ? SLIDE_CONFIG : undefined
        ) );
      } );
    }

    const frame_points = this.levelFramePoints( fit_tables, cube_size );
    const cannon_pivot = Cannon.composedPivot();

    this.camera_controller.fitAlong( this.table_position, frame_points, {
      frame_fill: GameScreen.LEVEL_FRAME_FILL,
      min_depth: this.camera_controller.composedDepth( cannon_pivot ) + GameScreen.MIN_SHOT_DEPTH,
      keep_above: { point: cannon_pivot, y: GameScreen.CANNON_MIN_Y },
      max_zoom: {
        size: cube_size.y,
        screen_fraction: GameScreen.MAX_CUBE_SCREEN_HEIGHT,
        at: this.tables.map( table => table.getTopCenter() ),
      },
      min_zoom: {
        size: cube_size.y,
        screen_fraction: GameScreen.MIN_CUBE_SCREEN_HEIGHT,
        overflow_fill: GameScreen.LEVEL_FRAME_OVERFLOW,
      },
    } );

    const view = this.camera_controller.getCamera().matrixWorldInverse;
    const farthest_depth = Math.max( 0, ...frame_points.map( p => -p.clone().applyMatrix4( view ).z ) );
    this.ground.followCamera( this.camera_controller.getHomeEye(), farthest_depth );

    const forward = this.camera_controller.getCamera().getWorldDirection( this.ball_far_dir ).setY( 0 ).normalize();
    this.ball_far_limit = frame_points.length
      ? Math.max( ...frame_points.map( p => p.dot( forward ) ) ) + GameScreen.BALL_FAR_MARGIN
      : Infinity;

    this.table_frames = this.tables.map( table => ( {
      table,
      top: table.getTopY(),
      center: table.getCenter(),
      size: table.getSize(),
      leg_size: table.getLegSize()
    } ) );

    // Seeded from the tabletop, then raised by each block as it is placed.
    // main seeded this from its single `table_top_y`; without a seed a level
    // with nothing on it reports a screen rect that stops at the world floor.
    this.tallest_block_top_y = Math.max(
      ...this.table_frames.map( frame => frame.top )
    );

    this.aim_body_handles = new Set( this.tables.flatMap( table => table.getBodyHandles() ) );
    this.top_body_to_table = new Map( this.tables.map( ( table, i ) => [ table.getTopBodyHandle(), i ] ) );
    this.carried_balls = this.tables.map( () => [] );

    this.field_rotators = this.tables.map(
      ( table, index ) => new FieldRotator( table, () => this.collectFieldBodies( index ) )
    );

    const rigs = new Map<string, PhysicsTable[]>();

    for ( const table of this.tables )
    {
      if ( !table.isHanging() )
        continue;

      const centre = table.getRestTopBox().getCenter( new THREE.Vector3() );
      const spot = `${centre.x.toFixed( 2 )}:${centre.z.toFixed( 2 )}`;

      ( rigs.get( spot ) ?? rigs.set( spot, [] ).get( spot )! ).push( table );
    }

    this.hanging_rigs = [ ...rigs.values() ].map( group => new HangingRig( group, HANG_CONFIG ) );

    this.table_swingers = this.hanging_rigs.map( rig => new TableSwinger(
      rig,
      HANG_CONFIG,
      table => this.collectFieldBodies( this.tables.indexOf( table ) )
    ) );

    this.table_elevators = this.tables.flatMap( ( table, index ) =>
      table.isElevating()
        ? [ new TableElevator( table, ELEVATOR_CONFIG, () => this.collectFieldBodies( index ) ) ]
        : []
    );

    this.table_sliders = this.tables.flatMap( ( table, index ) =>
      table.isSliding()
        ? [ new TableSlider( table, SLIDE_CONFIG, () => this.collectFieldBodies( index ) ) ]
        : []
    );

    if ( !GlobalSettings.IS_PROD )
      ( window as any ).__gameScreen = this;

    PhysicsWorld.setPreStepHook( dt =>
    {
      for ( const ball of this.cannon.getBalls() )
        ball.capturePrePhysicsVelocity();

      if ( this.field_rotators.some( r => r.isActive() )
        || this.table_swingers.length > 0
        || this.table_elevators.length > 0
        || this.table_sliders.length > 0 )
      {
        this.reassignBlockTables();
        this.updateCarriedBalls();
      }

      for ( const rotator of this.field_rotators )
        rotator.step( dt );

      for ( const swinger of this.table_swingers )
        swinger.step( dt );

      for ( const elevator of this.table_elevators )
        elevator.step( dt );

      for ( const slider of this.table_sliders )
        slider.step( dt );
    } );
  }

  private *collectFieldBodies( table_index: number ): Iterable<RAPIER.RigidBody>
  {
    for ( const e of this.entities )
      if ( GameScreen.hasBody( e ) && ( this.block_table_index.get( e ) ?? 0 ) === table_index )
        yield e.body;

    const balls = this.carried_balls[table_index];
    if ( balls )
      yield* balls;
  }

  // A block knocked off one table can physically land on a neighbouring one.
  // block_table_index is otherwise only ever set once, at creation — without
  // this, the block's original table stops carrying it (correctly, it's no
  // longer on that table) but the table it actually landed on never picks it
  // up either, since collectFieldBodies() still filters by the stale index.
  // The block then just sits frozen while the table rotates under it.
  private reassignBlockTables(): void
  {
    for ( const e of this.entities )
    {
      if ( !GameScreen.hasBody( e ) )
        continue;

      const pos = e.body.translation();
      let on = -1;
      let on_top = -Infinity;

      for ( let i = 0; i < this.tables.length; i++ )
      {
        if ( this.tables[i].isOutside( pos ) )
          continue;

        // Where the deck IS, not where it was laid out: when two tables share
        // a footprint, a raised one has to win the tie-break it would lose on
        // its resting height.
        const top = this.tables[i].getTopY() + this.tables[i].getElevation();

        if ( top > on_top )
        {
          on_top = top;
          on = i;
        }
      }

      if ( on >= 0 )
        this.block_table_index.set( e, on );
    }
  }

  // Balls touching a rotating table (its top or a carried block) must rotate
  // with it: the rotator moves blocks by teleport, so to the solver they are
  // static — a resting ball would otherwise be ploughed through by block
  // colliders sweeping into it. Airborne balls are never carried, so ballistic
  // arcs stay straight.
  private updateCarriedBalls(): void
  {
    if ( !this.cannon || this.carried_balls.length === 0 )
      return;

    for ( const list of this.carried_balls )
      list.length = 0;

    const world = PhysicsWorld.getWorld();

    for ( const ball of this.cannon.getBalls() )
    {
      if ( !ball.alive )
        continue;

      let table_index = -1;

      world.contactPairsWith( ball.collider, other =>
      {
        if ( table_index >= 0 )
          return;

        const top_table = this.top_body_to_table.get( other.parent()?.handle ?? -1 );

        if ( top_table !== undefined )
        {
          table_index = top_table;
          return;
        }

        const entity = this.by_collider.get( other.handle );

        if ( entity )
          table_index = this.block_table_index.get( entity ) ?? 0;
      } );

      if ( table_index >= 0 )
        this.carried_balls[table_index].push( ball.body );
    }
  }

  public setFieldRotation( deg: number, animate = false, table_index?: number ): void
  {
    for ( const rotator of this.rotatorsFor( table_index ) )
      rotator.setAngle( deg, animate ? {} : undefined );
  }

  public startFieldSpin( deg_per_sec: number, table_index?: number ): void
  {
    for ( const rotator of this.rotatorsFor( table_index ) )
      rotator.startSpin( deg_per_sec );
  }

  public stopFieldSpin( table_index?: number ): void
  {
    for ( const rotator of this.rotatorsFor( table_index ) )
      rotator.stopSpin();
  }

  public getFieldRotation( table_index = 0 ): number
  {
    return this.field_rotators[table_index]?.getAngleDeg() ?? 0;
  }

  private rotatorsFor( table_index?: number ): FieldRotator[]
  {
    if ( table_index === undefined )
      return this.field_rotators;

    const rotator = this.field_rotators[table_index];
    return rotator ? [ rotator ] : [];
  }

  private levelFramePoints( fit_tables: TableFit[], cube_size: THREE.Vector3 ): THREE.Vector3[]
  {
    const points: THREE.Vector3[] = [];
    const overhang = blockOverhang( cube_size );

    fit_tables.forEach( ( fit, i ) =>
    {
      const table = this.tables[i];
      if ( !table )
        return;

      const center = table.getTopCenter();
      const pivot = table.getPivot();
      const top_y = table.getTopY();
      const half_x = fit.claim.half_width + overhang.x;
      const half_z = fit.claim.half_depth + overhang.z;
      // An elevating table spends most of the level above where it was laid
      // out, so the frame is fitted to the top of its travel — otherwise its
      // tower climbs out of shot every stroke.
      const tower_top_y = top_y + table.getRiseHeight() + cube_size.y * towerTopHeight(
        fit.layers,
        fit.cols_limit,
        fit.layer_limit
      );

      const column = ( x: number, z: number ): void =>
      {
        points.push( new THREE.Vector3( x, top_y, z ), new THREE.Vector3( x, tower_top_y, z ) );
      };

      if ( fit.claim.rotates && ( fit.rotation?.spin ?? 0 ) !== 0 )
      {
        const segments = GameScreen.FRAME_DISC_SEGMENTS;
        const radius = Math.hypot(
          Math.abs( center.x - pivot.x ) + half_x,
          Math.abs( center.z - pivot.z ) + half_z
        ) / Math.cos( Math.PI / segments );

        for ( let k = 0; k < segments; k++ )
        {
          const angle = k / segments * Math.PI * 2;
          column( pivot.x + Math.cos( angle ) * radius, pivot.z + Math.sin( angle ) * radius );
        }

        return;
      }

      const angle = fit.claim.rotates ? THREE.MathUtils.degToRad( fit.rotation?.angle ?? 0 ) : 0;

      for ( const sx of [ -1, 1 ] )
        for ( const sz of [ -1, 1 ] )
        {
          const corner = new THREE.Vector3(
            center.x + sx * half_x - pivot.x,
            0,
            center.z + sz * half_z - pivot.z
          ).applyAxisAngle( GameScreen.UP, angle );

          column( pivot.x + corner.x, pivot.z + corner.z );
        }
    } );

    return points;
  }
  private createCannon(): void
  {
    const aim_plane = new THREE.Plane();

    aim_plane.setFromNormalAndCoplanarPoint(
      new THREE.Vector3( 0, 0, 1 ),
      new THREE.Vector3(
        this.table_position.x,
        this.tables[0].getTopY(),
        this.table_position.z
      )
    );

    this.cannon = new Cannon( aim_plane, collider => this.isAimTarget( collider ) );
    this.cannon.setFarLimit( this.ball_far_dir, this.ball_far_limit );

    if ( TextureTest.isActive() )
      this.cannon.setVisible( false );

    this.cannon.followView( point => this.camera_controller.fromComposedView( point ) );

    // The resting pose is a real ballistic aim at the centre of the third
    // block from the table. This keeps the visible barrel direction and the
    // shot trajectory in agreement before the player starts dragging.
    const table = this.tables[0];
    const center = table.getCenter();
    const size = table.getSize();
    const cube_size = PhysicsCube.getSize();
    this.cannon.aimAtWorldPoint( new THREE.Vector3(
      this.table_position.x,
      table.getTopY() + cube_size.y * 2.5,
      center.z + size.z * 0.5
    ) );
  }

  private isAimTarget( collider: RAPIER.Collider ): boolean
  {
    if ( this.by_collider.has( collider.handle ) )
      return true;

    const body_handle = collider.parent()?.handle;

    return body_handle !== undefined && this.aim_body_handles.has( body_handle );
  }

  private createStaticTowers( data: LevelData ): void
  {
    const center = this.tables[0].getCenter();
    const cube_size = PhysicsCube.getSize();

    const gap_x = cube_size.x * 0.15;
    const gap_z = cube_size.z * 0.15;

    const step_x = cube_size.x + gap_x;
    const step_z = cube_size.z + gap_z;

    const start_x = center.x - ( data.columns - 1 ) * step_x * 0.5;
    const start_z = center.z - ( data.rows - 1 ) * step_z * 0.5;

    for ( const tower of data.towers )
    {
      const x = start_x + tower.col * step_x;
      const z = start_z + tower.row * step_z;

      this.buildTowerFromStack( x, z, tower.stack );
    }
  }

  private buildTowerFromStack(
    x: number,
    z: number,
    stack: StackItem[]
  ): void
  {
    const cube_size = PhysicsCube.getSize().y;
    const step = cube_size;
    const table_top = this.tables[0].getTopY();

    let slot = 0;

    for ( const item of stack )
    {
      const blocks = item.blocks ?? 1;
      const slot_y = table_top + cube_size / 2 + slot * step;

      if ( item.type === "jar" )
      {
        this.addEntity(
          new JamJar(
            new THREE.Vector3(
              x,
              slot_y + ( blocks - 1 ) * step / 2,
              z
            ),
            1,
            blocks
          )
        );

        slot += blocks;
      }
      else
      {
        const axis = item.axis ?? "y";
        const width = axis === "x" ? blocks : 1;
        const height = axis === "y" ? blocks : 1;

        this.addEntity(
          new PhysicsCube(
            new THREE.Vector3(
              x,
              slot_y + ( axis === "y" ? ( blocks - 1 ) * step / 2 : 0 ),
              z
            ),
            width,
            height
          )
        );

        slot += axis === "y" ? blocks : 1;
      }
    }

    this.addEntity(
      new PhysicsCone(
        new THREE.Vector3(
          x,
          table_top + cube_size / 2 + slot * step,
          z
        )
      )
    );
  }

  private createRandomTowers(): void
  {
    const center = this.tables[0].getCenter();
    const size = this.tables[0].getSize();

    const rows = Math.min(
      1 + Math.floor( ( this.level - 1 ) / 2 ),
      2
    );

    const columns = Math.min(
      2 + Math.floor( ( this.level - 1 ) / 8 ),
      8
    );

    const cells: { x: number; z: number; col: number; row: number }[] = [];

    const cube_size = PhysicsCube.getSize();

    const gap_x = cube_size.x * 0.15;
    const gap_z = cube_size.z * 0.15;

    const step_x = cube_size.x + gap_x;
    const step_z = cube_size.z + gap_z;

    const start_x = center.x - ( columns - 1 ) * step_x * 0.5;
    const start_z = center.z - ( rows - 1 ) * step_z * 0.5;

    for ( let row = 0; row < rows; row++ )
    {
      for ( let col = 0; col < columns; col++ )
      {
        cells.push( {
          x: start_x + col * step_x,
          z: start_z + row * step_z,
          col,
          row
        } );
      }
    }

    const shuffled = shuffle(
      [...cells]
    );

    const min_towers = Math.min(
      1 + Math.floor( this.level / 2 ),
      cells.length
    );

    const max_towers = Math.min(
      min_towers + 2,
      cells.length
    );

    const tower_count = THREE.MathUtils.randInt(
      min_towers,
      max_towers
    );

    const towers = shuffled.slice( 0, tower_count );

    const occupied = new Set<string>();
    for ( const cell of towers )
      occupied.add( `${cell.col}:${cell.row}` );

    for ( const cell of towers )
    {
      const min_height = Math.min(
        2 + Math.floor( this.level / 8 ),
        8
      );

      const max_height = Math.min(
        min_height + 2,
        10
      );

      const height = THREE.MathUtils.randInt(
        min_height,
        max_height
      );

      const can_x =
        !occupied.has( `${cell.col - 1}:${cell.row}` ) &&
        !occupied.has( `${cell.col + 1}:${cell.row}` );

      this.createTower( cell.x, cell.z, height, can_x );
    }
  }

  private createLayeredBlocks(
    layers: LevelLayer[],
    table_index: number,
    cols_limit = Infinity,
    layer_limit = Infinity,
    shelf?: ShelfLimits
  ): void
  {
    const table = this.tables[table_index];
    const center = table.getTopCenter();
    const cube_size = PhysicsCube.getSize();
    // Surface height, not getTopY(): on a tilted table the model's AABB top is
    // the raised uphill corner, which would spawn the whole wall in mid-air.
    const table_top = table.getTopSurfaceY();

    const gap_x = cube_size.x * PhysicsCube.GRID_GAP_RATIO;
    const gap_z = cube_size.z * PhysicsCube.GRID_GAP_RATIO;
    const step_x = cube_size.x + gap_x;
    const step_z = cube_size.z + gap_z;

    const effective_layers = layers.slice( 0, layer_limit );
    const max_cols = Math.min( cols_limit, Math.max( ...effective_layers.map( l => l.grid[0]?.length ?? 0 ) ) );
    const start_z = center.z - ( effective_layers.length - 1 ) * step_z * 0.5;

    const placed: {
      piece: LevelPiece;
      position: THREE.Vector3;
      layer: LevelLayer;
      layer_index: number;
      col_offset: number;
    }[] = [];

    effective_layers.forEach( ( layer, layer_index ) =>
    {
      const cols = Math.min( cols_limit, layer.grid[0]?.length ?? 0 );
      const rows = layer.grid.length;
      const col_offset = ( max_cols - cols ) / 2;
      const start_x = center.x - ( max_cols - 1 ) * step_x * 0.5 + col_offset * step_x;
      const world_z = start_z + layer_index * step_z;

      layer.pieces.forEach( piece =>
      {
        if ( piece.x >= max_cols )
          return;

        const col_center = piece.x + ( piece.width - 1 ) / 2 + ( piece.offsetX ?? 0 );
        const height_index_center = rows - piece.y - ( piece.height + 1 ) / 2 + ( piece.offsetY ?? 0 );

        const position = new THREE.Vector3(
          start_x + col_center * step_x,
          table_top + cube_size.y / 2 + height_index_center * cube_size.y,

          world_z + ( piece.offsetZ ?? 0 ) * step_z
        );

        placed.push( { piece, position, layer, layer_index, col_offset } );
      } );
    } );

    if ( placed.length === 0 )
      return;

    let min_x = Infinity, max_x = -Infinity, min_z = Infinity, max_z = -Infinity;
    for ( const { piece, position } of placed )
    {
      const half_x = piece.width * step_x * 0.5;
      min_x = Math.min( min_x, position.x - half_x );
      max_x = Math.max( max_x, position.x + half_x );
      min_z = Math.min( min_z, position.z - step_z * 0.5 );
      max_z = Math.max( max_z, position.z + step_z * 0.5 );
    }

    const correction_x = center.x - ( min_x + max_x ) / 2;
    const correction_z = center.z - ( min_z + max_z ) / 2;

    let dropped_by_shelf = 0;

    const tilt_rad = -table.getTiltRad();
    const tilt_pivot = new THREE.Vector3( center.x, table_top, center.z );

    placed.forEach( ( { piece, position, layer, layer_index, col_offset } ) =>
    {
      position.x += correction_x;
      position.z += correction_z;

      if ( tilt_rad !== 0 )
        position.sub( tilt_pivot ).applyAxisAngle( GameScreen.TILT_AXIS, tilt_rad ).add( tilt_pivot );

      if ( shelf && GameScreen.blockedByShelf( shelf, position, piece, cube_size, step_x, step_z ) )
      {
        dropped_by_shelf++;
        return;
      }

      this.tallest_block_top_y = Math.max(
        this.tallest_block_top_y,
        position.y + ( piece.height / 2 ) * cube_size.y
      );

      const entity_count = this.entities.length;

      const authored_rotation = GameScreen.authoredRotation( piece );
      const tilted_rotation = GameScreen.tiltedRotation( authored_rotation, tilt_rad );

      switch ( piece.type )
      {
        case LevelPieceType.Cube:
          {
            const variant_index = piece.variant !== undefined ? GameScreen.CUBE_SKIN_VARIANT_INDEX[piece.variant] : undefined;
            this.addEntity( new PhysicsCube( position, piece.width, piece.height, [ CubeMaterialType.PLYWOOD, CubeMaterialType.BRICK ], variant_index, tilted_rotation, tilt_rad ), table_index );
            break;
          }

        case LevelPieceType.Stone:
          this.addEntity( new PhysicsCube(
            position,
            piece.width,
            piece.height,
            CubeMaterialType.STONE,
            undefined,
            tilted_rotation,
            tilt_rad
          ), table_index );
          break;

        case LevelPieceType.Column:
          this.addEntity( new PhysicsCube(
            position,
            piece.width,
            piece.height,
            CubeMaterialType.COLUMN,
            undefined,
            tilted_rotation,
            tilt_rad
          ), table_index );
          break;

        case LevelPieceType.RoundColumn:
          this.addEntity( new PhysicsCube(
            position,
            piece.width,
            piece.height,
            CubeMaterialType.ROUND_COLUMN,
            undefined,
            tilted_rotation,
            tilt_rad
          ), table_index );
          break;

        case LevelPieceType.Plastic:
          this.addEntity( new PhysicsCube(
            position,
            piece.width,
            piece.height,
            CubeMaterialType.PLASTIC,
            piece.variant === undefined ? undefined : GameScreen.PLASTIC_SKIN_VARIANT_INDEX[piece.variant],
            tilted_rotation,
            tilt_rad
          ), table_index );
          break;

        case LevelPieceType.Jar:
          this.addEntity( new JamJar( position, piece.width, piece.height, piece.variant, piece.lying, tilted_rotation, tilt_rad ), table_index );
          break;

        case LevelPieceType.Cone:
          this.addEntity( new PhysicsCone( position, piece.variant, tilted_rotation, tilt_rad ), table_index );
          break;
        case LevelPieceType.Metal:
          this.addEntity( new MetalBlock( position, piece.width, piece.height, piece.variant, undefined, piece.lying, tilted_rotation, tilt_rad ), table_index );
          break;
        case LevelPieceType.RoundMetal:
          this.addEntity( new MetalBlock( position, piece.width, piece.height, piece.variant, MetalBlockShape.Round, piece.lying, tilted_rotation, tilt_rad ), table_index );
          break;
        case LevelPieceType.IronCan:
          this.addEntity( new MetalBlock( position, piece.width, piece.height, piece.variant, MetalBlockShape.Iron, piece.lying, tilted_rotation, tilt_rad ), table_index );
          break;
        case LevelPieceType.Ice:
          this.addEntity( new IceBlock( position, piece.width, piece.height, piece.lying, tilted_rotation, tilt_rad ), table_index );
          break;
        case LevelPieceType.Tree:
          this.addEntity(
            new TreeBlock( position, piece.width, piece.height, piece.lying, tilted_rotation, tilt_rad ),
            table_index
          );
          break;
        case LevelPieceType.Watermelon:
          this.addEntity( new WatermelonBlock( position, tilted_rotation, tilt_rad ), table_index );
          break;
      }

      if ( BlockShowcase.isActive() && this.entities.length > entity_count )
        BlockShowcase.attachLabel( piece.id, this.entities[this.entities.length - 1] );

      if ( this.entities.length > entity_count )
      {
        const entity = this.entities[this.entities.length - 1];
        const cells: { col: number; row: number }[] = [];

        for ( let row = 0; row < layer.grid.length; row++ )
        {
          for ( let col = 0; col < layer.grid[row].length; col++ )
          {
            if ( layer.grid[row][col] !== piece.id )
              continue;

            cells.push( {
              col: col + col_offset + ( piece.offsetX ?? 0 ),
              row: layer.grid.length - 1 - row + ( piece.offsetY ?? 0 )
            } );
          }
        }

        this.block_grid_placement.set( entity, {
          table_index,
          layer: layer_index + ( piece.offsetZ ?? 0 ),
          cells
        } );
      }
    } );

    if ( dropped_by_shelf !== 0 )
      console.warn(
        `[GameScreen] table ${table_index}: ${dropped_by_shelf} piece(s) dropped — ` +
        "they did not fit under the shelf standing on it, or stood in its leg"
      );
  }

  private createTower(
    x: number,
    z: number,
    height: number,
    can_x: boolean
  ): void
  {
    const cube_size = PhysicsCube.getSize().y;
    const gap = 0;
    const step = cube_size + gap;
    const table_top = this.tables[0].getTopY();

    const slots = height + 1;
    const guaranteed_jar_slot = THREE.MathUtils.randInt( 0, slots - 1 );

    for ( let slot = 0; slot < slots; slot++ )
    {
      const slot_y = table_top + cube_size / 2 + slot * step;

      const is_jar =
        slot === guaranteed_jar_slot ||
        Math.random() < this.JAR_SPAWN_CHANCE;

      if ( is_jar )
      {
        const available = slots - slot;

        let blocks = 1;

        if ( available >= 3 && Math.random() < this.JAR_TRIPLE_CHANCE )
          blocks = 3;
        else if ( available >= 2 && Math.random() < this.JAR_DOUBLE_CHANCE )
          blocks = 2;

        this.addEntity(
          new JamJar(
            new THREE.Vector3(
              x,
              slot_y + ( blocks - 1 ) * step / 2,
              z
            ),
            1,
            blocks
          )
        );

        slot += blocks - 1;
      }
      else
      {
        const available = slots - slot;

        if ( Math.random() < this.ICE_SPAWN_CHANCE )
        {
          let ice_height = 1;

          if ( available >= 3 && Math.random() < this.ICE_TRIPLE_CHANCE )
            ice_height = 3;
          else if ( available >= 2 && Math.random() < this.ICE_DOUBLE_CHANCE )
            ice_height = 2;

          this.addEntity(
            new IceBlock(
              new THREE.Vector3(
                x,
                slot_y + ( ice_height - 1 ) * step / 2,
                z
              ),
              1,
              ice_height
            )
          );

          slot += ice_height - 1;
          continue;
        }

        if ( Math.random() < this.METAL_SPAWN_CHANCE )
        {
          let metal_height = 1;

          if ( available >= 3 && Math.random() < this.METAL_TRIPLE_CHANCE )
            metal_height = 3;
          else if ( available >= 2 && Math.random() < this.METAL_DOUBLE_CHANCE )
            metal_height = 2;

          this.addEntity(
            new MetalBlock(
              new THREE.Vector3(
                x,
                slot_y + ( metal_height - 1 ) * step / 2,
                z
              ),
              1,
              metal_height
            )
          );

          slot += metal_height - 1;
          continue;
        }

        let blocks = 1;

        if ( Math.random() < this.CUBE_TRIPLE_CHANCE )
          blocks = 3;
        else if ( Math.random() < this.CUBE_DOUBLE_CHANCE )
          blocks = 2;

        const axes: ( "x" | "y" )[] = [];

        if ( blocks > 1 )
        {
          if ( available >= blocks )
            axes.push( "y" );

          if ( can_x )
            axes.push( "x" );
        }

        if ( axes.length === 0 )
        {
          blocks = 1;
          axes.push( "y" );
        }

        const axis = axes[Math.floor( Math.random() * axes.length )];
        const width = axis === "x" ? blocks : 1;
        const height = axis === "y" ? blocks : 1;

        this.addEntity(
          new PhysicsCube(
            new THREE.Vector3(
              x,
              slot_y + ( axis === "y" ? ( blocks - 1 ) * step / 2 : 0 ),
              z
            ),
            width,
            height
          )
        );

        if ( axis === "y" )
          slot += blocks - 1;
      }
    }

    this.addEntity(
      new PhysicsCone(
        new THREE.Vector3(
          x,
          table_top + cube_size / 2 + slots * step,
          z
        )
      )
    );
  }

  // private explodeJar( jar: JamJar, dropped = false ): void
  // {
  //   const position = jar.getPosition();

  //   if ( dropped )
  //     jar.playDropSound();
  //   else
  //     jar.playHitSound();

  //   jar.destroy();

  //   VfxManager.addJamSplash(
  //     position,
  //     jar.getJamColor(),
  //     jar.size.y * 0.5,
  //     jar.getHeightBlocks()
  //   );
  //   void hapticsController.vibrate( HapticPattern.MEDIUM, VibrationContext.GENERAL );
  // }

  // private explodeIceBlock( ice: IceBlock, dropped = false ): void
  // {
  //   const position = ice.getPosition();

  //   ice.playHitSound();

  //   const raw_speed = ice.getImpactSpeed();
  //   const impact_speed = Math.min( 4.5, Math.max( 2.5, raw_speed * 0.3 ) );

  //   ice.destroy( 0.0, impact_speed );

  //   VfxManager.addCannonBallImpact2( position );
  //   void hapticsController.vibrate( HapticPattern.MEDIUM, VibrationContext.GENERAL );
  // }

  // private wakeBlockStructure(): void
  // {
  //   for ( const cube of this.cubes )
  //     cube.body.wakeUp();

  //   for ( const jar of this.jars )
  //     jar.body.wakeUp();

  //   for ( const cone of this.cones )
  //     cone.body.wakeUp();

  //   for ( const metal_block of this.metal_blocks )
  //     metal_block.body.wakeUp();

  //   for ( const ice of this.ice_blocks )
  //     ice.body.wakeUp();

  //   for ( const tree of this.tree_blocks )
  //     tree.body.wakeUp();

  //   for ( const melon of this.watermelons )
  //     melon.wakeUp();
  // }

  public onExit(): void
  {
    if ( this.is_challenge )
    {
      ScreenManager.showScreen( Screens.DAILY_CHALLENGE );
      return;
    }

    LeaveLevelDialog.open( () => this.gameLose() );
  }

  // public update( dt: number ): void
  // {
  //   this.ground.update( dt );

  //   // OPTIMIZATION: Refactoring, separate the update loop into separate loops for each type of object, change filter to maybe splice

  //   for ( const cube of this.cubes )
  //   {
  //     cube.checkDestroy( dt, this.table );
  //     cube.update( dt );
  //   }

  //   for ( const cone of this.cones )
  //   {
  //     cone.checkDestroy( dt, this.table );
  //     cone.update( dt );
  //   }

  //   for ( const metal_block of this.metal_blocks )
  //   {
  //     metal_block.checkDestroy( dt, this.table );
  //     metal_block.update( dt );
  //   }

  //   for ( const ice of this.ice_blocks )
  //   {
  //     ice.update( dt );

  //     if ( !ice.isAlive() )
  //       continue;

  //     const bottom_y = ice.getPosition().y - ice.size.y * 0.5;
  //     const hit_floor = bottom_y < 0.2;

  //     if ( hit_floor )
  //     {
  //       this.explodeIceBlock( ice, hit_floor );
  //     }
  //     else if ( ice.isArmed() && ice.getImpactSpeed() >= IceBlock.MIN_IMPACT_SPEED )
  //     {
  //       if ( ice.isCracked() )
  //         this.explodeIceBlock( ice, false );
  //       else
  //         ice.crack();
  //     }
  //   }

  //   for ( const jar of this.jars )
  //   {
  //     jar.update( dt );

  //     if ( !jar.isAlive() )
  //       continue;

  //     const bottom_y = jar.getPosition().y - jar.size.y * 0.5;
  //     const hit_floor = bottom_y < 0.2;

  //     if (
  //       hit_floor ||
  //       ( jar.isArmed() &&
  //         jar.getImpactSpeed() >= JamJar.MIN_BREAK_IMPACT_SPEED )
  //     )
  //     {
  //       this.explodeJar( jar, hit_floor );
  //     }
  //   }

  //   if ( this.shots_left === 0 )
  //     this.cannon.forceFadeOutBalls();

  //   this.cubes = this.cubes.filter( cube =>
  //   {
  //     if ( cube.isAlive() )
  //       return true;

  //     cube.dispose();

  //     return false;
  //   } );

  //   this.jars = this.jars.filter( jar =>
  //   {
  //     if ( jar.isAlive() )
  //       return true;

  //     jar.dispose();

  //     return false;
  //   } );

  //   this.cones = this.cones.filter( cone =>
  //   {
  //     if ( cone.isAlive() )
  //       return true;

  //     cone.dispose();

  //     return false;
  //   } );

  //   this.metal_blocks = this.metal_blocks.filter( metal_block =>
  //   {
  //     if ( metal_block.isAlive() ) return true;
  //     metal_block.dispose();
  //     return false;
  //   } );

  //   this.ice_blocks = this.ice_blocks.filter( ice =>
  //   {
  //     if ( ice.isAlive() ) return true;
  //     ice.dispose();
  //     return false;
  //   } );

  //   if ( !this.is_finished )
  //   {
  //     if (
  //       this.cubes.length === 0 &&
  //       this.jars.length === 0 &&
  //       this.cones.length === 0 &&
  //       this.metal_blocks.length === 0 &&
  //       this.ice_blocks.length === 0
  //     )
  //     {
  //       this.gameWin();
  //     }
  //     else if (
  //       this.shots_left === 0 &&
  //       !this.cannon.hasActiveBalls() &&
  //       this.allCubesSettled()
  //     )
  //     {
  //       this.is_finished = true;

  //       if ( this.simulation )
  //       {
  //         this.finishSimulation( false );
  //       }
  //       else
  //       {
  //         void this.playResultSoundThenMusic( AudioClips.LOSE_GAME );

  //         const showRetryDialog = () =>
  //         {
  //           PlayDialog.open(
  //             this.level,
  //             PlayDialogState.TryAgain,
  //             getLevelDifficulty( this.level ),
  //             () => this.gameLose(),
  //             () => this.retryGame()
  //           ) );
  //         };

  //         void getReviveOffer()
  //           .then( revive =>
  //           {
  //             if ( !revive.available || !revive.offer )
  //             {
  //               showRetryDialog();
  //               return;
  //             }

  //             OverlayFactory.openOverlay( OverlayId.CONTINUE_OVERLAY, {
  //               offer: revive.offer,
  //               purchase_index: revive.purchase_index,
  //               onClose: showRetryDialog,
  //               onBuy: ( shots: number ) =>
  //               {
  //                 this.is_finished = false;
  //                 this.addShots( shots );
  //                 void audioController.playMusic(
  //                   this.level % 4 === 0 ? AudioClips.MUSIC_4 : AudioClips.MUSIC_3,
  //                   true
  //                 );
  //               },
  //             } as ContinueOverlayParams );
  //           } )
  //           .catch( e =>
  //           {
  //             console.warn( '[Economy] revive offer failed', e );
  //             showRetryDialog();
  //           } );
  //       }
  //     }
  //   }

  //   const table_top = this.table.getTopY();
  //   const table_center = this.table.getCenter();
  //   const table_size = this.table.getSize();

  //   // Check collision between balls, table, and cubes
  //   for ( const ball of this.cannon.getBalls() )
  //   {
  //     // OPTIMIZATION: Only check for collisions if the ball is above the table

  //     if ( !ball.has_hit_table )
  //     {
  //       const ball_pos = ball.body.translation();

  //       // 1. Table Top Collision
  //       const is_above_table =
  //         Math.abs( ball_pos.x - table_center.x ) <= ( table_size.x / 2 + 0.5 ) &&
  //         Math.abs( ball_pos.z - table_center.z ) <= ( table_size.z / 2 + 0.5 );

  //       const hits_table_top = is_above_table && Math.abs( ball_pos.y - table_top ) <= 0.36;

  //       // 2. Table Leg Collision (leg is centered at table_center and has half-extents 0.6 along X and Z)
  //       // Leg half-extent (0.6) + ball radius (0.35) = 0.95. We use 0.96 as a threshold.
  //       const is_colliding_with_leg_xz =
  //         Math.abs( ball_pos.x - table_center.x ) <= 0.96 &&
  //         Math.abs( ball_pos.z - table_center.z ) <= 0.96;
  //       const hits_leg = ball_pos.y < table_top && is_colliding_with_leg_xz;

  //       if ( hits_table_top || hits_leg )
  //       {
  //         ball.has_hit_table = true;
  //         const spawn_pos = hits_table_top
  //           ? new THREE.Vector3( ball_pos.x, table_top, ball_pos.z )
  //           : new THREE.Vector3( ball_pos.x, ball_pos.y, ball_pos.z );

  //         VfxManager.addCannonBallImpact1( spawn_pos );
  //       }
  //     }

  //     if ( ball.has_exploded )
  //       continue;

  //     // OPTIMIZATION: Using contactPairsWith

  //     for ( const jar of this.jars )
  //     {
  //       if ( !jar.isAlive() )
  //         continue;

  //       const contact_point = ball.contactPointWith( jar.collider );
  //       if ( contact_point )
  //       {
  //         ball.has_exploded = true;
  //         this.explodeJar( jar );
  //         break;
  //       }
  //     }

  //     if ( ball.has_exploded )
  //       continue;

  //     for ( const ice of this.ice_blocks )
  //     {
  //       if ( !ice.isAlive() || !ice.isArmed() )
  //         continue;

  //       const contact_point = ball.contactPointWith( ice.collider );
  //       if ( contact_point )
  //       {
  //         ball.has_exploded = true;
  //         if ( ice.isCracked() )
  //           this.explodeIceBlock( ice );
  //         else
  //           ice.crack();
  //         break;
  //       }
  //     }

  //     if ( ball.has_exploded )
  //       continue;

  //     for ( const cube of this.cubes )
  //     {
  //       const contact_point = ball.contactPointWith( cube.collider );
  //       if ( contact_point )
  //       {
  //         ball.has_exploded = true;
  //         this.wakeBlockStructure(); //
  //         cube.playHitSound();
  //         void hapticsController.vibrate( HapticPattern.MEDIUM, VibrationContext.GENERAL );
  //         VfxManager.addCannonBallImpact2( contact_point );
  //         break;
  //       }
  //     }

  //     if ( ball.has_exploded )
  //       continue;

  //     for ( const cone of this.cones )
  //     {
  //       const contact_point = ball.contactPointWith( cone.collider );
  //       if ( contact_point )
  //       {
  //         ball.has_exploded = true;
  //         cone.playHitSound();
  //         void hapticsController.vibrate( HapticPattern.MEDIUM, VibrationContext.GENERAL );
  //         VfxManager.addCannonBallImpact2( contact_point );
  //         break;
  //       }
  //     }

  //     if ( ball.has_exploded )
  //       continue;

  //     for ( const metal_block of this.metal_blocks )
  //     {
  //       const contact_point = ball.contactPointWith( metal_block.collider );
  //       if ( contact_point )
  //       {
  //         ball.has_exploded = true;
  //         metal_block.playHitSound();
  //         void hapticsController.vibrate( HapticPattern.MEDIUM, VibrationContext.GENERAL );
  //         VfxManager.addCannonBallImpact2( contact_point );
  //         break;
  //       }
  //     }
  //   }

  //   this.cannon.update( dt );
  //   VfxRuntime.update( dt );
  //   this.bot?.update( dt );
  // }

  public update( dt: number ): void
  {
    if ( !this.camera_controller )
      return;

    this.booster_manager.update( dt );
    this.updateStickyMines( dt );

    this.ground.update( dt );

    this.updateEntities( dt );

    if ( this.level_built_pending )
    {
      this.level_built_pending = false;
      this.onLevelBuilt();
    }

    this.updateBarriers( dt );
    this.processBallCollisions();

    this.cleanupDeadEntities();
    this.checkGameEnd( dt );

    this.cannon.update( dt );
    if ( !GlobalSettings.DEBUG_DISABLE_VFX3D )
      Vfx3DRuntime.update( dt );
    this.bot?.update( dt );
    this.updateStressAutofire( dt );

    BlockShowcase.updateLabels();

    this.updateShadowFreshness( dt );
  }

  private stress_fire_timer = 0;
  private stress_fire_index = 0;

  private updateStressAutofire( dt: number ): void
  {
    if ( !StressTest.isAutofiring() || this.is_finished || this.simulation )
      return;

    this.stress_fire_timer -= dt;

    if ( this.stress_fire_timer > 0 )
      return;

    this.stress_fire_timer = StressTest.FIRE_INTERVAL;

    const targets = this.getAliveTargets();

    if ( targets.length === 0 )
      return;

    const target = targets[this.stress_fire_index % targets.length];
    this.stress_fire_index += 7;

    this.cannon.fireAtWorldPoint( target.position.clone() );
    this.onShot();
  }

  private shadow_idle_timer = 0;
  private shadow_refresh_parity = 0;
  private shadow_entity_count = -1;
  private graphics_sweep_timer = 0;
  private static readonly GRAPHICS_SWEEP_INTERVAL = 1;
  private shadow_poses = new WeakMap<THREE.Object3D, { x: number; y: number; z: number; qx: number; qy: number; qz: number; qw: number }>();

  private static readonly SHADOW_POS_EPS = 0.004;
  private static readonly SHADOW_ROT_EPS = 0.0025;

  // Rebuilding the shadow map is ~4ms of the frame, so it is only requested
  // while the scene can still change. Positions come from the meshes physics
  // already synced: querying Rapier allocates a vector per body per frame, and
  // its sleep flag is useless here because CCD bodies in contact never sleep.
  public static shadow_debug = { frames: 0, refresh: 0, cannon: 0, count: 0, debris: 0, entities: 0 };

  private updateShadowFreshness( dt: number ): void
  {
    const dbg = GameScreen.shadow_debug;
    dbg.frames++;

    FpsOverlay.markLive(
      this.cannon ? this.cannon.getBalls().length : 0,
      VfxBlockShatterTask.hasLiveDebris() ? 1 : 0,
      VfxBlockShatterTask.mapsCreated()
    );

    let moving = false;

    const entity_count = this.entities.length + this.tree_blocks.length + this.watermelons.length;
    this.graphics_sweep_timer += dt;
    if ( entity_count > this.shadow_entity_count
      || ( this.graphics_sweep_timer >= GameScreen.GRAPHICS_SWEEP_INTERVAL && GraphicsSettings.downgradesMaterials() ) )
    {
      GraphicsApply.applyScene( SceneManager.world );
      this.graphics_sweep_timer = 0;
    }
    if ( entity_count !== this.shadow_entity_count )
    {
      this.shadow_entity_count = entity_count;
      moving = true;
      dbg.count++;
    }

    if ( !moving && VfxBlockShatterTask.hasLiveDebris() )
    {
      moving = true;
      dbg.debris++;
    }

    if ( !moving )
    {
      moving = this.haveEntitiesMoved();
      if ( moving ) dbg.entities++;
    }

    this.shadow_idle_timer = moving ? 0 : this.shadow_idle_timer + dt;

    if ( this.shadow_idle_timer < 0.5 )
    {
      const divisor = Math.max( 1, GraphicsSettings.profile().shadow_refresh_divisor );
      this.shadow_refresh_parity = ( this.shadow_refresh_parity + 1 ) % divisor;
      if ( this.shadow_refresh_parity === 0 )
      {
        dbg.refresh++;
        Renderer.requestShadowUpdate();
        this.captureEntityPoses();
      }
    }
    else
      this.shadow_refresh_parity = 0;
  }

  private meshMoved( mesh: THREE.Object3D ): boolean
  {
    const prev = this.shadow_poses.get( mesh );
    if ( !prev ) return true;

    const p = mesh.position;
    if ( Math.abs( p.x - prev.x ) > GameScreen.SHADOW_POS_EPS ) return true;
    if ( Math.abs( p.y - prev.y ) > GameScreen.SHADOW_POS_EPS ) return true;
    if ( Math.abs( p.z - prev.z ) > GameScreen.SHADOW_POS_EPS ) return true;

    const q = mesh.quaternion;
    if ( Math.abs( q.x - prev.qx ) > GameScreen.SHADOW_ROT_EPS ) return true;
    if ( Math.abs( q.y - prev.qy ) > GameScreen.SHADOW_ROT_EPS ) return true;
    if ( Math.abs( q.z - prev.qz ) > GameScreen.SHADOW_ROT_EPS ) return true;
    if ( Math.abs( q.w - prev.qw ) > GameScreen.SHADOW_ROT_EPS ) return true;

    return false;
  }

  private haveEntitiesMoved(): boolean
  {
    for ( let i = 0; i < this.entities.length; i++ )
      if ( this.meshMoved( this.entities[i].mesh ) ) return true;

    for ( let i = 0; i < this.tree_blocks.length; i++ )
      if ( this.meshMoved( this.tree_blocks[i].mesh ) ) return true;

    for ( let i = 0; i < this.watermelons.length; i++ )
      if ( this.meshMoved( this.watermelons[i].mesh ) ) return true;

    const balls = this.cannon.getBalls();
    for ( let i = 0; i < balls.length; i++ )
      if ( this.meshMoved( balls[i].mesh ) ) return true;

    return false;
  }

  private capturePose( mesh: THREE.Object3D ): void
  {
    const p = mesh.position;
    const q = mesh.quaternion;
    const prev = this.shadow_poses.get( mesh );

    if ( prev )
    {
      prev.x = p.x; prev.y = p.y; prev.z = p.z;
      prev.qx = q.x; prev.qy = q.y; prev.qz = q.z; prev.qw = q.w;
      return;
    }

    this.shadow_poses.set( mesh, { x: p.x, y: p.y, z: p.z, qx: q.x, qy: q.y, qz: q.z, qw: q.w } );
  }

  private captureEntityPoses(): void
  {
    for ( let i = 0; i < this.entities.length; i++ )
      this.capturePose( this.entities[i].mesh );

    for ( let i = 0; i < this.tree_blocks.length; i++ )
      this.capturePose( this.tree_blocks[i].mesh );

    for ( let i = 0; i < this.watermelons.length; i++ )
      this.capturePose( this.watermelons[i].mesh );

    const balls = this.cannon.getBalls();
    for ( let i = 0; i < balls.length; i++ )
      this.capturePose( balls[i].mesh );
  }

  private checkTableHit( ball: CannonBall ): void
  {
    if ( ball.has_hit_table )
      return;

    const p = ball.body.translation();
    const r = ball.getRadius();

    for ( const frame of this.table_frames )
    {
      // Every table is tested, and each in its OWN unrotated frame: a spinning
      // table's footprint no longer lines up with the world axes, so the ball
      // has to be brought back into the table's coordinates before the box test.
      const local = frame.table.toUnrotatedFrame( p, this.ball_local_pos );

      const is_above_table =
        Math.abs( local.x - frame.center.x ) <= ( frame.size.x / 2 + r + 0.15 ) &&
        Math.abs( local.z - frame.center.z ) <= ( frame.size.z / 2 + r + 0.15 );

      // local.y, not p.y: toUnrotatedFrame takes the swing and the lift back
      // off, so frame.top — measured once, at rest — stays the right thing to
      // compare a moving table's deck against.
      const hits_table_top = is_above_table && Math.abs( local.y - frame.top ) <= 0.36;

      // Match the impact/VFX test to the actual scaled leg bounds.
      const is_colliding_with_leg_xz =
        Math.abs( local.x - frame.center.x ) <= frame.leg_size.x * 0.5 + 0.35 &&
        Math.abs( local.z - frame.center.z ) <= frame.leg_size.z * 0.5 + 0.35;

      // A hanging table reports no leg at all; without this the margin
      // alone would still make a strip of empty air under it hittable.
      const hits_leg = local.y < frame.top && frame.leg_size.x > 0 && is_colliding_with_leg_xz;

      if ( !hits_table_top && !hits_leg )
        continue;

      ball.has_hit_table = true;

      this.tmp_impact.set( p.x, hits_table_top ? frame.top : p.y, p.z );

      if ( ball.canHitMultipleTargets )
      {
        if ( !ball.has_triggered_big_ball_impact )
        {
          ball.has_triggered_big_ball_impact = true;
          void audioController.playSound( AudioClips.BIG_SHOT_HIT );
        }
      }
      Vfx3DManager.addCannonBallTableImpact( this.tmp_impact );
      return;
    }
  }

  private processBallCollisions(): void
  {
    const world = PhysicsWorld.getWorld();

    for ( const ball of this.cannon.getBalls() )
    {
      if ( ball.has_exploded && !ball.canHitMultipleTargets )
        continue;

      this.checkTableHit( ball );

      let firework_hit: { target: LevelEntity; point: THREE.Vector3 } | null = null;
      let hit_pass_through_jar = false;

      world.contactPairsWith( ball.collider, other =>
      {
        if ( ball.has_exploded && !ball.canHitMultipleTargets )
          return;

        const target = this.by_collider.get( other.handle );

        if (
          !target
          || !GameScreen.hasBody( target )
          || ( ball.isFirework ? !target.isAlive() : !target.canBeHit() )
        )
          return;

        const point = ball.contactPointWith( other );

        if ( !point || !ball.registerTargetHit( other ) )
          return;

        const passes_through_jar = target.kind === EntityKind.Jar && ball.tryUseJarPenetration();
        if ( !ball.canHitMultipleTargets && !passes_through_jar )
          ball.has_exploded = true;
        if ( passes_through_jar )
          hit_pass_through_jar = true;
        if ( ball.isFirework )
        {
          // Destroying the target and the rocket mutates the physics world
          // (removeRigidBody), which is forbidden inside contactPairsWith —
          // the wasm World is still borrowed by the iteration and Rapier
          // throws "recursive use of an object". Only record the hit here;
          // it is applied right after the iteration returns.
          if ( !firework_hit )
            firework_hit = { target, point };
        }
        else
        {
          if ( ball.canHitMultipleTargets )
          {
            Vfx3DManager.suppress_cannon_ball_impact = true;
            target.onBallHit( point );
            Vfx3DManager.suppress_cannon_ball_impact = false;

            if ( !( target instanceof IceBlock ) && !ball.has_triggered_big_ball_impact )
            {
              ball.has_triggered_big_ball_impact = true;
              void audioController.playSound( AudioClips.BIG_SHOT_HIT );
              Vfx3DManager.addBigBallBlockImpact( point );
            }
          }
          else
          {
            target.onBallHit( point );
          }
        }

        if ( !target.isAlive() )
          this.needs_cleanup = true;
      } );

      if ( hit_pass_through_jar )
        ball.preserveVelocityAfterJarHit();

      if ( firework_hit )
      {
        const { target, point } = firework_hit as { target: LevelEntity; point: THREE.Vector3 };

        void audioController.playSound( AudioClips.ROCKET_HIT, false, 0.3 );
        Vfx3DManager.addFireworkExplosion( point );
        this.breakFireworkArea( target, point );
        ball.detonate();
      }
    }
  }

  private allEntitiesSettled(): boolean
  {
    for ( const e of this.entities )
      if ( e.isAtRest && !e.isAtRest() )
        return false;

    return true;
  }

  private updateEntities( dt: number ): void
  {
    for ( const e of this.entities )
    {
      e.checkSelfDestruct( dt, this.tableFor( e ) );
      e.update( dt );

      if ( !e.isAlive() )
        this.needs_cleanup = true;
    }
  }

  private updateBarriers( dt: number ): void
  {
    for ( const barrier of this.barriers )
      barrier.update( dt );
  }

  private checkGameEnd( dt: number ): void
  {
    if ( this.debug_empty_level )
      return;

    if ( this.is_finished )
      return;

    if ( !this.entities.some( e => e.isAlive() ) )
    {
      this.gameWin();
      return;
    }

    if ( !GlobalSettings.IS_PROD )
    {
      this.debug_end_state_timer += dt;
      if ( this.debug_end_state_timer >= 2 )
      {
        this.debug_end_state_timer = 0;
        console.log(
          `[GameEnd] entities=${this.entities.length} shots_left=${this.shots_left} ` +
          `infinite_balls=${this.infinite_balls_active} multiball=${this.multiball_active} ` +
          `fireworks=${this.fireworks_active} cannon_active_balls=${this.cannon.hasActiveBalls()} ` +
          `settled=${this.allEntitiesSettled()}`
        );
      }
    }

    if ( this.shots_left !== 0 || this.infinite_balls_active || this.multiball_active || this.fireworks_active || this.cannon.hasActiveBalls() )
    {
      this.loss_settle_timer = 0;
      return;
    }

    if ( !this.allEntitiesSettled() )
    {
      // Tiny residual physics motion can otherwise postpone the lose dialog
      // for many seconds after the final ball has disappeared.
      if ( this.simulation )
        return;

      this.loss_settle_timer += dt;
      if ( this.loss_settle_timer < GameScreen.LOSS_SETTLE_TIMEOUT )
        return;
    }

    this.is_finished = true;
    this.closeBoosterHighlight();

    if ( this.simulation )
    {
      this.finishSimulation( false );
      return;
    }

    void this.playResultSoundThenMusic( AudioClips.LOSE_GAME );

    const showRetryDialog = () =>
    {
      if ( this.is_challenge )
      {
        DailyChallengePlayDialog.open(
          this.level,
          PlayDialogState.TryAgain,
          () => this.gameLose(),
          () => this.retryGame()
        );
        return;
      }

      PlayDialog.open(
        this.level,
        PlayDialogState.TryAgain,
        getLevelDifficulty( this.level ),
        this.is_challenge,
        () => this.gameLose(),
        () => this.retryGame()
      );
    };

    // Declining the continue overlay is what actually breaks a running tower
    // streak, so warn about it there — with one last chance to buy the same
    // revive offer — before falling through to the usual retry dialog.
    const showTowerFailThenRetry = ( offer: ReviveOfferDto, purchase_index: number ) =>
    {
      const streak = this.towerFailStreak();
      if ( !streak )
      {
        showRetryDialog();
        return;
      }

      TowerFailDialog.open( {
        ...streak,
        offer,
        purchase_index,
        on_close: showRetryDialog,
        on_buy: ( shots: number ) => this.resumeAfterRevive( shots ),
      } );
    };

    void getReviveOffer()
      .then( revive =>
      {
        if ( !revive.available || !revive.offer )
        {
          showRetryDialog();
          return;
        }

        const offer = revive.offer;

        OverlayFactory.openOverlay( OverlayId.CONTINUE_OVERLAY, {
          offer,
          purchase_index: revive.purchase_index,
          level: this.level,
          onClose: () => showTowerFailThenRetry( offer, revive.purchase_index ),
          onBuy: ( shots: number ) => this.resumeAfterRevive( shots ),
        } as ContinueOverlayParams );
      } )
      .catch( e =>
      {
        console.warn( '[Economy] revive offer failed', e );
        showRetryDialog();
      } );
  }

  private cleanupDeadEntities(): void
  {
    if ( !this.needs_cleanup )
      return;

    this.needs_cleanup = false;

    let write = 0;

    for ( let read = 0; read < this.entities.length; read++ )
    {
      const e = this.entities[read];

      if ( e.isAlive() || e.keepCorpse?.() )
      {
        this.entities[write++] = e;
        continue;
      }

      this.by_collider.delete( e.collider.handle );
      e.dispose();
    }

    this.entities.length = write;
  }

  private onPointerDown = (
    event: PointerEvent
  ): void =>
  {
    if ( BoosterHighlightRenderer.isShown() )
      return;

    if ( Game.instance.isLoadingGameScreenShown() )
      return;

    if ( DialogManager.isAnyDialogOpened() || OverlayManager.isAnyOverlayOpened() )
      return;

    if ( this.is_finished )
      return;

    if ( this.input_blocked_by_tutorial || this.input_blocked_by_level_start_booster )
      return;

    if ( this.blocked_tap_hit_test?.( event.clientX, event.clientY ) )
      return;

    if ( this.onAimStart() )
      return;

    this.aiming = true;
    this.cannon.aim( event );
  }

  private onPointerMove = (
    event: PointerEvent
  ): void =>
  {
    if ( BoosterHighlightRenderer.isShown() )
      return;

    if ( Game.instance.isLoadingGameScreenShown() )
    {
      this.aiming = false;
      return;
    }

    if ( !this.aiming )
      return;

    this.cannon.aim( event );
  }

  private onPointerUp = (
    event: PointerEvent
  ): void =>
  {
    if ( BoosterHighlightRenderer.isShown() )
      return;

    this.finishAim( event );
  }

  private finishAim( event: PointerEvent ): boolean
  {
    if ( Game.instance.isLoadingGameScreenShown() )
    {
      this.aiming = false;
      return false;
    }

    if ( !this.aiming || this.is_finished )
    {
      return false;
    }

    this.aiming = false;

    // The timed effect may expire between pointer-down and pointer-up.
    if ( this.shots_left === 0 && !this.infinite_balls_active )
    {
      return false;
    }

    if ( this.isBelowTableShotLimit( event ) )
    {
      this.onInvalidShot( event );
      void audioController.playSound( AudioClips.UI_CLICK );
      return false;
    }

    const is_booster_shot = this.booster_manager.hasActiveBooster();
    const is_firework_shot = this.fireworks_active;
    const is_multiball_shot = this.multiball_active;
    const is_big_ball_shot = this.cannon_ball_size_multiplier > 1;
    let target: THREE.Vector3 | null;
    if ( is_firework_shot )
    {
      const random_target = this.getRandomFireworksTargets( 1 )[0];
      if ( !random_target )
      {
        return false;
      }

      target = new THREE.Vector3( random_target.x, random_target.y, random_target.z );
      this.cannon.fireAtWorldPoint( target, 1, 1, true );
      void audioController.playSound( AudioClips.BIG_SHOT );
      void audioController.playSound( AudioClips.ROCKET_FLY );
    }
    else
    {
      target = this.cannon.fire(
        event,
        this.cannon_ball_size_multiplier,
        this.cannon_ball_mass_multiplier * (
          this.multiball_active ? this.booster_manager.getMultiballMassMultiplier() : 1
        ),
        false
      );
    }
    if ( target === null )
    {
      return false;
    }


    this.booster_manager.onPrimaryBallFired( target );

    if ( !is_firework_shot )
      void audioController.playSound(
        is_multiball_shot
          ? AudioClips.TEN_SHOTS_SINGLE
          : is_big_ball_shot
            ? AudioClips.BIG_SHOT
            : AudioClips.CANNON_SHOT
      );

    this.onShot( !is_booster_shot );
    return true;
  }

  private isBelowTableShotLimit(
    event: PointerEvent
  ): boolean
  {
    let max_front_z = -Infinity;
    let min_bottom_y = Infinity;

    for ( const table of this.tables )
    {
      const center = table.getCenter();
      const size = table.getSize();
      const angle = table.getFieldAngle();
      const front_half_z = (
        Math.abs( Math.sin( angle ) ) * size.x
        + Math.abs( Math.cos( angle ) ) * size.z
      ) * 0.5;

      max_front_z = Math.max( max_front_z, center.z + front_half_z );
      min_bottom_y = Math.min( min_bottom_y, table.getPlatformBottomY() );
    }

    const table_bottom_front = new THREE.Vector3(
      this.table_position.x,
      // Keep the no-shot boundary slightly above the visual bottom edge, at
      // roughly the middle of the lower tabletop trim.
      min_bottom_y + 0.15,
      max_front_z
    );

    const screen_pos = table_bottom_front.project(
      SceneManager.camera
    );

    const limit_y = ( -screen_pos.y * 0.5 + 0.5 ) * window.innerHeight;

    return event.clientY > limit_y;
  }

  private allCubesSettled(): boolean
  {
    for ( const cube of this.cubes )
    {
      if ( !cube.isAtRest() )
        return false;
    }

    for ( const cone of this.cones )
    {
      if ( !cone.isAtRest() )
        return false;
    }

    for ( const metal_block of this.metal_blocks )
    {
      if ( !metal_block.isAtRest() )
        return false;
    }

    for ( const ice of this.ice_blocks )
    {
      if ( !ice.isAtRest() )
        return false;
    }

    return true;
  }

  private gameWin(): void
  {
    if ( this.is_finished )
      return;

    this.is_finished = true;
    this.attempt_completed = true;
    this.closeBoosterHighlight();

    if ( this.simulation )
    {
      this.finishSimulation( true );
      return;
    }

    // Keep the final destruction visible briefly before the result overlay
    // covers the playfield.
    this.win_result_timeout = window.setTimeout( () =>
    {
      this.win_result_timeout = null;
      this.finishGameWin();
    }, this.WIN_RESULT_DELAY_MS );
  }

  private finishGameWin(): void
  {
    if ( this.is_challenge )
    {
      this.finishChallengeWin();
      return;
    }

    // Playtesting a level from the editor must not touch the player's progress, the
    // leaderboard or the funnel — just say it was cleared and offer another run.
    if ( StressTest.isAutofiring() )
    {
      void ScreenManager.showScreen( Screens.GAME, { level: StressTest.currentLevel() } );
      return;
    }

    if ( GameScreen.isDebugRun() )
    {
      void this.playResultSoundThenMusic( AudioClips.WIN_GAME, AudioClips.WIN_GAME_LOOP );
      this.showResultOverlay( {
        level: this.level,
        difficulty: getLevelDifficulty( this.level ),
        title_key: "RESULT.LEVEL_PASSED",
        stars: this.STARS_PER_WIN,
        rewards: [],
        onContinue: () =>
        {
          if ( StressTest.isActive() )
            void ScreenManager.showScreen( Screens.GAME, { level: StressTest.advance() } );
          else if ( TextureTest.isActive() )
            void ScreenManager.showScreen( Screens.GAME, { level: TextureTest.currentLevel() } );
          else if ( TableFormationShowcase.isActive() )
            void ScreenManager.showScreen( Screens.GAME, { level: TableFormationShowcase.advance() } );
          else if ( BarrierShowcase.isActive() )
            void ScreenManager.showScreen( Screens.GAME, { level: BarrierShowcase.advance() } );
          else
            TestLevelBridge.restart();
        },
      } );
      return;
    }

    const current_level = StatisticsStorage.load().level;
    StatisticsStorage.update( { level: current_level + 1 } );
    recordInterstitialLevelPassed( current_level );
    GameScreen.resetConsecutiveAttempts();
    void LeaderboardSyncService.submitScore( this.STARS_PER_WIN );
    this.trackGameFinish( AnalyticsLevelResult.WIN );
    void logGameEnd( {
      result: 'win',
      score: this.getAnalyticsScore(),
      duration: this.getAnalyticsElapsedSeconds(),
    } );
    void logLevelCompleted( {
      level: this.level,
      score: this.getAnalyticsScore(),
      attempts: takeLevelAttempts( this.level ),
    } );
    void this.playResultSoundThenMusic( AudioClips.WIN_GAME, AudioClips.WIN_GAME_LOOP );

    const overlay = this.showResultOverlay( {
      level: this.level,
      difficulty: getLevelDifficulty( this.level ),
      title_key: "RESULT.LEVEL_PASSED",
      stars: this.STARS_PER_WIN,
      rewards: [],
      rewarded_level: current_level,
      onDoubleReward: async () =>
      {
        const r = await doubleLevelReward( current_level, this.STARS_PER_WIN );
        adoptEconomyFromPlayer( r.player );
        void logVirtualCurrencyEarned( {
          currency_name: 'coins',
          amount: r.granted.coins,
          source: 'level_reward_x2',
        } );
        void logVirtualCurrencyEarned( {
          currency_name: 'stars',
          amount: r.granted.points,
          source: 'level_reward_x2',
        } );
        void logRewardedCompleted( {
          placement: 'level_reward_x2',
          reward_type: 'stars',
          reward_value: this.STARS_PER_WIN,
        } );
        return r.granted.coins;
      },
      onContinue: () =>
      {
        if ( isHardUpdatePending() )
        {
          void ScreenManager.showScreen( Screens.MENU, { preserve_vfx: true } );
          return;
        }

        const menu_after_level = AppConfigService.get().menu_after_level;
        if ( menu_after_level > 0 && current_level >= menu_after_level )
        {
          preloadAdsInBackground();
          void ScreenManager.showScreen( Screens.MENU, { preserve_vfx: true } );
          this.maybeShowRateDialog( current_level );
          return;
        }

        const next_level = current_level + 1;
        const open_new_goal = StatisticsStorage.load().new_goal_pending;
        if ( open_new_goal )
          StatisticsStorage.update( { new_goal_pending: false } );

        preloadAdsInBackground();
        const next_level_offline_missing = !NetworkManager.isOnline() && !LevelPackService.isLevelCached( next_level );
        if ( current_level >= getMaxCampaignLevel() || !hasCampaignLevel( next_level ) || next_level_offline_missing )
        {
          void ScreenManager.showScreen( Screens.MENU, { preserve_vfx: true } ).then( () =>
          {
            explainMissingCampaignLevel( next_level );
          } );
          return;
        }
        void ScreenManager.showScreen( Screens.GAME, { level: next_level, preserve_vfx: true } ).then( () =>
        {
          if ( open_new_goal )
            OverlayFactory.openOverlay( OverlayId.NEW_GOAL_OVERLAY, {
              unlocks: LevelPackService.getUnlocks( next_level ),
            } );
        } );

        this.maybeShowRateDialog( current_level );
      },
    } );

    if ( this.winContinuesToMenu( current_level ) )
      ScreenManager.prebuildMenu();

    TournamentService.addCrowns( TournamentService.crownsForWin( getLevelDifficulty( current_level ) ) );
    this.reportLevelCompleteToServer( current_level, overlay );
  }

  private winContinuesToMenu( current_level: number ): boolean
  {
    if ( isHardUpdatePending() )
      return true;
    const menu_after_level = AppConfigService.get().menu_after_level;
    if ( menu_after_level > 0 && current_level >= menu_after_level )
      return true;
    const next_level = current_level + 1;
    return current_level >= getMaxCampaignLevel() || !hasCampaignLevel( next_level )
      || ( !NetworkManager.isOnline() && !LevelPackService.isLevelCached( next_level ) );
  }

  private finishChallengeWin(): void
  {
    const stats = StatisticsStorage.load();

    const completed_days = Math.min( DAILY_CHALLENGE_DAYS_COUNT, Math.max( stats.daily_challenge_completed_days, this.level ) );
    StatisticsStorage.update( { daily_challenge_completed_days: completed_days } );

    // The server is the source of truth for the progress and pays out the
    // stage rewards; the fly-to-HUD animation below is only the presentation.
    void DailyChallengeService.complete( this.level );

    recordInterstitialLevelPassed();
    this.trackGameFinish( AnalyticsLevelResult.WIN );
    void logGameEnd( {
      result: 'win',
      score: this.getAnalyticsScore(),
      duration: this.getAnalyticsElapsedSeconds(),
    } );

    void this.playResultSoundThenMusic( AudioClips.WIN_GAME, AudioClips.WIN_GAME_LOOP );

    this.showResultOverlay( {
      level: this.level,
      difficulty: PlayDialogDifficulty.SuperHard,
      title_key: "RESULT.LEVEL_PASSED",
      stars: this.STARS_PER_WIN,
      daily_challenge_num: this.level,
      rewards: DailyChallengeService.getRewards( this.level ).map( reward => ( {
        currency: reward.type,
        amount: reward.amount,
      } ) ),
      onContinue: () =>
      {
        void ScreenManager.showScreen( Screens.DAILY_CHALLENGE );
      },
    } );
  }

  private gameLose(): void
  {
    // Failing a playtest costs nothing: no heart, no analytics, no ad — just reload it.
    if ( GameScreen.isDebugRun() )
    {
      if ( StressTest.isActive() )
        void ScreenManager.showScreen( Screens.GAME, { level: StressTest.currentLevel() } );
      else if ( TextureTest.isActive() )
        void ScreenManager.showScreen( Screens.GAME, { level: TextureTest.currentLevel() } );
      else if ( TableFormationShowcase.isActive() )
        void ScreenManager.showScreen( Screens.GAME, { level: TableFormationShowcase.currentLevel() } );
      else if ( BarrierShowcase.isActive() )
        void ScreenManager.showScreen( Screens.GAME, { level: BarrierShowcase.currentLevel() } );
      else
        TestLevelBridge.restart();
      return;
    }

    this.spendHeartOnLevelFail();
    this.trackGameFinish( AnalyticsLevelResult.LOSE );
    void logGameEnd( {
      result: 'lose',
      score: this.getAnalyticsScore(),
      duration: this.getAnalyticsElapsedSeconds(),
    } );

    ScreenManager.showScreen( this.is_challenge ? Screens.DAILY_CHALLENGE : Screens.MENU );
    void tryShowInterstitial( 'level_loss', this.interstitialLevel() );
  }

  private async retryGame(): Promise<void>
  {
    this.trackGameFinish( AnalyticsLevelResult.LOSE );
    void logGameEnd( {
      result: 'lose',
      score: this.getAnalyticsScore(),
      duration: this.getAnalyticsElapsedSeconds(),
    } );
    await this.spendHeartOnLevelFail();
  }

  private interstitialLevel(): number | undefined
  {
    return this.is_challenge ? undefined : this.level;
  }

  private async playResultSoundThenMusic(
    result_sound: AudioClips,
    followup_music: AudioClips = AudioClips.MUSIC_1
  ): Promise<void>
  {
    audioController.stopMusic();
    await audioController.playSound( result_sound );
    await new Promise<void>( resolve => setTimeout( resolve, 1500 ) );

    if ( this.is_finished && ScreenManager.getCurrentScreen() === Screens.GAME )
      await audioController.playMusic( followup_music, true );
  }

  private maybeShowRateDialog( completed_level: number ): void
  {
    const config = AppConfigService.get().rate_dialog;

    if ( !config.enabled || completed_level < config.min_level )
      return;

    const stats = StatisticsStorage.load();
    if ( stats.rate_dialog_show_count >= config.max_shows )
      return;

    if ( config.stop_after_rating && stats.rate_dialog_completed )
      return;

    if (
      stats.rate_dialog_last_shown_level > 0 &&
      completed_level - stats.rate_dialog_last_shown_level < config.repeat_after_levels
    )
      return;

    StatisticsStorage.update( {
      rate_dialog_show_count: stats.rate_dialog_show_count + 1,
      rate_dialog_last_shown_level: completed_level,
    } );

    setTimeout( () => RateGameDialog.open( {
      onRatingSubmitted: () =>
      {
        if ( config.stop_after_rating )
          StatisticsStorage.update( { rate_dialog_completed: true } );
      },
    } ), config.show_delay_ms );
  }

  // ================== [SIM] API для бота ==================

  public hasActiveBalls(): boolean
  {
    return this.cannon.hasActiveBalls();
  }

  public areTargetsSettled(): boolean
  {
    return this.allEntitiesSettled();
  }

  public getAliveTargets(): BotTarget[]
  {
    const targets: BotTarget[] = [];

    for ( const e of this.entities )
    {
      if ( !e.isAlive() )
        continue;

      const kind = e.kind === EntityKind.Jar ? "jar"
        : e.kind === EntityKind.Cone ? "cone"
          : "cube";

      targets.push( {
        kind,
        position: e.mesh.position,
        size: e.size,
        table: this.block_table_index.get( e ) ?? 0,
      } );
    }

    return targets;
  }

  public isReachable( world_point: THREE.Vector3 ): boolean
  {
    return this.cannon.isPathClear( world_point );
  }

  public botFireAt( world_point: THREE.Vector3 ): void
  {
    this.cannon.fireAtWorldPoint( world_point );
    this.onShot();
  }

  private finishSimulation( won: boolean ): void
  {
    this.is_finished = true;

    this.on_simulation_finished( {
      won,
      shots_used: this.bot?.getShotsDone() ?? 0,
      sim_seconds: 0, // заповнює SimulationRunner
    } );
  }

  private showResultOverlay( params: GameResultOverlayParams ): GameResultOverlay | null
  {
    // Never leave a booster highlight active underneath the result overlay.
    this.closeBoosterHighlight();
    this.onResultOverlayShown();
    const overlay = OverlayFactory.openOverlay( OverlayId.GAME_RESULT_OVERLAY, params );
    return overlay instanceof GameResultOverlay ? overlay : null;
  }

  private closeBoosterHighlight(): void
  {
    BoosterHighlightRenderer.hide();
    this.onAttemptFinished();
  }

  private async spendHeartOnLevelFail(): Promise<void>
  {
    // Playtesting must never cost the tester a life.
    if ( GameScreen.isDebugRun() )
      return;

    if ( this.is_challenge )
      return;

    if ( !this.heart_settlement )
      this.heart_settlement = this.reportHeartSettlement();

    await this.heart_settlement;
  }

  private async reportHeartSettlement(): Promise<void>
  {
    try
    {
      const r = await reportLevelFailed();
      WinStreakTowerService.applyProgress( r.win_streak_tower );
      StatisticsStorage.update( {
        hearts: r.hearts,
        max_hearts: r.max_hearts,
        next_heart_at: nextHeartAtMs( r.next_heart_at, 0 ),
      } );
      void OfferTriggerController.report( 'level_failed', { level: this.level } );
      if ( r.hearts <= 0 )
        void OfferTriggerController.report( 'hearts_depleted', { level: this.level } );
      void logVirtualCurrencySpent( {
        currency_name: 'hearts',
        amount: 1,
        sink: 'level_failed',
      } );
    }
    catch ( e )
    {
      console.warn( '[Economy] level-failed report failed', e );
    }
  }

  private reportLevelCompleteToServer( level: number, overlay: GameResultOverlay | null ): void
  {
    completeLevel( level, this.STARS_PER_WIN )
      .then( r =>
      {
        WinStreakTowerService.applyProgress( r.win_streak_tower );
        overlay?.setWinStreakTowerProgress( r.win_streak_tower );
        TournamentService.applyWinReport( r.tournament );
        adoptEconomyFromPlayer( r.player );
        void OfferTriggerController.report( 'level_won', { level } );

        if ( !r.granted )
          return;

        if ( level === 5 || level === 10 )
        {
          const event_name = level === 5 ? 'level_5_complete' : 'level_10_complete';
          CustomEventsController.queueS2SEvent( event_name, {
            level,
            stars: this.STARS_PER_WIN,
            coins_granted: r.granted.coins,
            points_granted: r.granted.points,
            validation_source: 'economy_level_complete',
          } );
        }

        console.log( `[Economy] granted +${r.granted.coins} coins, +${r.granted.points} stars` );
        void logVirtualCurrencyEarned( {
          currency_name: 'coins',
          amount: r.granted.coins,
          source: 'level_completed',
        } );
        void logVirtualCurrencyEarned( {
          currency_name: 'stars',
          amount: r.granted.points,
          source: 'level_completed',
        } );
        overlay?.setRewards( [{ currency: "coin", amount: r.granted.coins }] );
      } )
      .catch( e => console.warn( '[Economy] level-complete failed', e ) );
  }

  private onShot( consume_cannon_ball = true ): void
  {
    this.analytics_shots_fired++;

    if ( !consume_cannon_ball || this.infinite_balls_active )
      return;

    this.shots_left--;

    this.onShotsLeftChanged(
      this.shots_left
    );
  }

  private trackGameFinish( result: AnalyticsLevelResult ): void
  {
    // Editor playtests are not real sessions — keep them out of the funnel entirely.
    // Guarded here as well as at the call sites, so exiting a playtest via destroy() or
    // any future path cannot leak an event either.
    if ( this.analytics_finish_tracked || GameScreen.isDebugRun() )
      return;

    this.analytics_finish_tracked = true;

    analytics.track( AnalyticsEventName.GAME_FINISH, {
      [AnalyticsPropertyKey.USERLEVEL]: this.level,
      [AnalyticsPropertyKey.GAMETYPE]: AnalyticsGameType.ROYAL_SMASH,
      [AnalyticsPropertyKey.GAME_NUMBER]: this.analytics_game_number,
      [AnalyticsPropertyKey.LEVEL_ID]: String( this.level ),
      [AnalyticsPropertyKey.DIFFICULTY]: GameScreen.toAnalyticsDifficulty( getLevelDifficulty( this.level ) ),
      [AnalyticsPropertyKey.START_MOVES]: this.analytics_start_moves,
      [AnalyticsPropertyKey.LEVEL_UID]: this.analytics_level_uid,
      [AnalyticsPropertyKey.GAMEID]: this.analytics_game_id,
      [AnalyticsPropertyKey.RESULT]: result,
      [AnalyticsPropertyKey.RESULT_POINTS]: this.analytics_initial_cube_count - this.countScoreTargets(),
      [AnalyticsPropertyKey.CELLS]: this.analytics_initial_cube_count,
      [AnalyticsPropertyKey.MOVES_DONE_V2]: this.analytics_shots_fired,
      [AnalyticsPropertyKey.MOVES_PURCHASED_TIMES]: this.analytics_moves_purchased_times,
      [AnalyticsPropertyKey.ELEMENTS_LEFT]: this.countScoreTargets(),
      [AnalyticsPropertyKey.BOOSTERS_USED_FIREWORK]: this.booster_manager.getUsedCount( CumulativeBoosterID.Fireworks ),
      [AnalyticsPropertyKey.BOOSTERS_USED_FIVE_BALLS]: this.booster_manager.getUsedCount( CumulativeBoosterID.Multiball ),
      [AnalyticsPropertyKey.BOOSTERS_USED_BIG_BALL]: this.booster_manager.getUsedCount( CumulativeBoosterID.BigBall ),
      [AnalyticsPropertyKey.BOOSTERS_USED_INFINITE_SHOTS]: this.booster_manager.getUsedCount( CumulativeBoosterID.InfiniteBalls ),
      [AnalyticsPropertyKey.GAMETIME]: Math.round( performance.now() - this.analytics_game_started_at ),
      ...this.analytics_mass,
      ...balanceSnapshot(),
    } );
  }

  private getAnalyticsScore(): number
  {
    return this.analytics_initial_cube_count - this.countScoreTargets();
  }

  private getAnalyticsElapsedSeconds(): number
  {
    return Math.max( 0, Math.round( ( performance.now() - this.analytics_game_started_at ) / 1000 ) );
  }

  public addShots( amount: number ): void
  {
    const added_shots = Math.max( 0, Math.floor( amount ) );
    if ( added_shots > 0 )
      this.analytics_moves_purchased_times++;
    this.shots_left += added_shots;
    this.onShotsLeftChanged( this.shots_left );
  }

  /** Puts the level back in play after a bought revive, whoever sold it. */
  private resumeAfterRevive( shots: number ): void
  {
    this.is_finished = false;
    this.loss_settle_timer = 0;
    this.addShots( shots );
    void audioController.playMusic(
      this.level % 4 === 0 ? AudioClips.MUSIC_4 : AudioClips.MUSIC_3,
      true
    );
  }

  /**
   * The tower streak this loss is about to break, or null when there is
   * nothing worth warning about. Read from the cache on purpose: the failure
   * is only reported to the server later, from spendHeartOnLevelFail(), so
   * the cached tower still holds the streak as it stands before the loss.
   */
  private towerFailStreak(): WinStreakProgress | null
  {
    // The tower ignores challenge runs and playtests, same as the heart spend.
    if ( this.is_challenge || GameScreen.isDebugRun() )
      return null;

    const cached = WinStreakTowerService.getCached();
    if ( cached?.enabled !== true )
      return null;

    const streak = getWinStreakProgress( cached.tower );
    if ( !streak || streak.wins_progress < GameScreen.MIN_TOWER_FAIL_STREAK )
      return null;

    return streak;
  }

  public getShotsLeft(): number
  {
    return this.shots_left;
  }

  public getLevel(): number
  {
    return this.level;
  }

  public getTableCount(): number
  {
    return this.tables.length;
  }

  public isEligibleForTutorial(): boolean
  {
    return this.level === 1
      && !this.simulation
      && !GameScreen.isDebugRun()
      && !StatisticsStorage.load().tutorial_completed;
  }

  public getBlockClientRects(): { x: number, y: number, width: number, height: number }[]
  {
    const bounds = Renderer.renderer.domElement.getBoundingClientRect();
    const camera = SceneManager.camera;
    camera.updateMatrixWorld( true );

    const box = new THREE.Box3();
    const corner = new THREE.Vector3();
    const rects: { x: number, y: number, width: number, height: number }[] = [];

    for ( const entity of this.entities )
    {
      if ( entity.kind === EntityKind.Flag || !entity.isAlive() )
        continue;

      entity.mesh.updateMatrixWorld( true );
      box.setFromObject( entity.mesh );
      if ( box.isEmpty() )
        continue;

      let min_x = Infinity;
      let max_x = -Infinity;
      let min_y = Infinity;
      let max_y = -Infinity;

      for ( const x of [ box.min.x, box.max.x ] )
      for ( const y of [ box.min.y, box.max.y ] )
      for ( const z of [ box.min.z, box.max.z ] )
      {
        corner.set( x, y, z ).project( camera );
        const px = bounds.left + ( corner.x * 0.5 + 0.5 ) * bounds.width;
        const py = bounds.top + ( -corner.y * 0.5 + 0.5 ) * bounds.height;
        min_x = Math.min( min_x, px );
        max_x = Math.max( max_x, px );
        min_y = Math.min( min_y, py );
        max_y = Math.max( max_y, py );
      }

      rects.push( { x: min_x, y: min_y, width: max_x - min_x, height: max_y - min_y } );
    }

    return rects;
  }

  public getTableScreenRect(): { x: number, y: number, width: number, height: number } | null
  {
    if ( this.tables.length === 0 )
      return null;

    const center = this.tables[0].getCenter();
    const size = this.tables[0].getSize();
    const y_bottom = this.tables[0].getPlatformBottomY();
    const y_top = this.tallest_block_top_y;

    const bounds = Renderer.renderer.domElement.getBoundingClientRect();

    let min_x = Infinity;
    let max_x = -Infinity;
    let min_y = Infinity;
    let max_y = -Infinity;

    for ( const dx of [ -0.5, 0.5 ] )
    for ( const dz of [ -0.5, 0.5 ] )
    for ( const y of [ y_bottom, y_top ] )
    {
      const corner = new THREE.Vector3( center.x + dx * size.x, y, center.z + dz * size.z );
      const ndc = corner.project( SceneManager.camera );
      const px = bounds.left + ( ndc.x * 0.5 + 0.5 ) * bounds.width;
      const py = bounds.top + ( -ndc.y * 0.5 + 0.5 ) * bounds.height;

      min_x = Math.min( min_x, px );
      max_x = Math.max( max_x, px );
      min_y = Math.min( min_y, py );
      max_y = Math.max( max_y, py );
    }

    return { x: min_x, y: min_y, width: max_x - min_x, height: max_y - min_y };
  }

  public completeTutorial(): void
  {
    const was_completed = StatisticsStorage.load().tutorial_completed;
    StatisticsStorage.update( { tutorial_completed: true } );
    if ( !was_completed )
      CustomEventsController.queueS2SEvent( 'tutorial_complete', { tutorial_version: 'gameplay-v1' } );
    void logAfCompleteTutorial();
  }

  public completeBoosterTutorial( booster_id: CumulativeBoosterID ): void
  {
    completeBoosterTutorial( booster_id );
  }

  public nameUI(): Screens
  {
    return Screens.GAME;
  }

  public destroy(): void
  {
    GraphicsSettings.unsubscribe( this.graphics_listener );
    this.booster_manager.destroy();
    this.clearStickyMines();
    this.closeBoosterHighlight();

    if ( this.win_result_timeout !== null )
    {
      window.clearTimeout( this.win_result_timeout );
      this.win_result_timeout = null;
    }

    if (
      !this.simulation
      && !GameScreen.isDebugRun()
      && !this.attempt_completed
    )
    {
      // Covers navigation paths outside the in-game exit dialog. The server
      // settles the active attempt with compare-and-swap, so this is safe even
      // when an explicit loss request is already in flight.
      void this.spendHeartOnLevelFail();
    }

    if (
      !this.is_finished
      && !this.simulation
      && !this.analytics_finish_tracked
      && !GameScreen.isDebugRun()
    )
    {
      this.trackGameFinish( AnalyticsLevelResult.EXIT );
      void logGameExit( {
        reason: 'user_exit',
        elapsed_time: this.getAnalyticsElapsedSeconds(),
        level: this.level,
      } );
    }

    this.is_finished = true;

    BlockShowcase.clearLabels();
    TextureTest.hideBar();

    PhysicsWorld.setPreStepHook( null );

    if ( !this.simulation )
    {
      window.removeEventListener(
        "pointerdown",
        this.onPointerDown
      );

      window.removeEventListener(
        "pointermove",
        this.onPointerMove
      );

      window.removeEventListener(
        "pointerup",
        this.onPointerUp
      );
    }

    for ( const e of this.entities )
      e.dispose();

    this.entities.length = 0;
    this.by_collider.clear();

    for ( const tree of this.tree_blocks )
    {
      tree.dispose();
    }

    this.tree_blocks = [];

    for ( const melon of this.watermelons )
    {
      melon.dispose();
    }

    this.watermelons = [];

    for ( const barrier of this.barriers )
      barrier.dispose();

    this.barriers = [];

    this.cannon.dispose();

    // [SIM][ВАЖЛИВО] прибрати фізичні тіла стола та землі, інакше за 1000
    // прогонів у Rapier-світі накопичиться 1000 столів (див. пояснення в чаті)
    for ( const table of this.tables )
      table.dispose();
    this.tables = [];
    this.field_rotators = [];
    this.table_swingers = [];
    this.table_elevators = [];
    this.table_sliders = [];
    for ( const rig of this.hanging_rigs )
      rig.dispose();
    this.hanging_rigs = [];
    this.aim_body_handles.clear();
    this.top_body_to_table.clear();
    this.carried_balls = [];
    this.table_frames = [];
    this.ground.dispose();

    this.sun?.dispose();

    Vfx3DRuntime.reset();

    SceneManager.world.clear();
  }
}
